<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AiBotSettingsAPI from 'dashboard/api/aiBotSettings';
import CommercialFlowsEditor from '../components/CommercialFlowsEditor.vue';

const route = useRoute();
const router = useRouter();

const loading = ref(false);
const saving = ref(false);
const error = ref('');
const success = ref('');

const company = ref(null);

const evolutionStatus = ref(null);

const aiSettings = ref({
  enabled: false,
  model: '',
  assistant_name: '',
  commercial_team_id: null,
  financial_team_id: null,
  support_team_id: null,
  cancellation_team_id: null,
  general_team_id: null,
  allowed_inbox_ids: [],

  auto_transfer: true,
  add_waiting_label: true,
  stop_ai_after_transfer: true,

  transfer_message: '',
  system_prompt: '',

  usage_profile: 'balanced',
  history_limit: 15,
  max_output_tokens: 700,
  monthly_cost_limit_brl: 0,
  monthly_limit_action: 'alert',

  local_replies_enabled: true,

    company_variables: {
      company_name: '',
      phone: '',
      whatsapp: '',
      address: '',
      business_hours: '',
      site: '',
      email: '',
    },


  commercial_flows: {},

  local_reply_rules: {
    greeting: {
      enabled: true,
      phrases: [
        'oi',
        'ola',
        'olá',
        'oie',
        'oii',
        'oiii',
        'bom dia',
        'boa tarde',
        'boa noite',
      ],
      reply:
        '{saudacao}! Eu sou {assistente}. Como posso ajudar você?',
    },

    thanks: {
      enabled: true,
      phrases: [
        'obrigado',
        'obrigada',
        'muito obrigado',
        'muito obrigada',
        'valeu',
      ],
      reply:
        'Por nada! Se precisar de mais alguma coisa, estou à disposição.',
    },

    farewell: {
      enabled: true,
      phrases: [
        'tchau',
        'até mais',
        'ate mais',
        'até logo',
        'ate logo',
      ],
      reply:
        'Até mais! Foi um prazer ajudar você.',
    },
  },
});

const companyUsage = ref({
  today: {
    cost_brl: 0,
    requests: 0,
  },
  month: {
    cost_brl: 0,
    requests: 0,
    input_tokens: 0,
    cached_input_tokens: 0,
    output_tokens: 0,
  },
  total: {
    cost_brl: 0,
    requests: 0,
  },
  usd_brl_rate: 0,
  current_month: '',
  history: [],
});

const operationalHistory = ref([]);
const operationalHistoryLoading = ref(false);
const operationalHistoryError = ref('');

const localUsage = ref({
  today: {
    responses: 0,
  },
  month: {
    responses: 0,
    by_type: {},
  },
  total: {
    responses: 0,
  },
  ai_month_requests: 0,
  total_automated: 0,
  local_percentage: 0,
  current_month: '',
});

const usageLoading = ref(false);

const aiSaving = ref(false);
const aiError = ref('');
const aiSuccess = ref('');
const localRulesImportInput = ref(null);
const selectedLocalTemplate = ref('');
const companyInboxes = ref([]);
const companyTeams = ref([]);
const evolutionPhone = ref('');
const evolutionQr = ref('');
const evolutionLoading = ref(false);
const evolutionError = ref('');

let evolutionPollTimer = null;

const form = ref({
  name: '',
  responsible: '',
  email: '',
  phone: '',
});

const companyId = computed(() =>
  Number(route.params.companyId)
);

const formatBRL = value => {
  return Number(value || 0).toLocaleString(
    'pt-BR',
    {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }
  );
};

const averageCost = (cost, requests) => {
  const count = Number(requests || 0);

  if (!count) {
    return 0;
  }

  return Number(cost || 0) / count;
};

const monthlyLimitPercent = computed(() => {
  const limit =
    Number(
      aiSettings.value.monthly_cost_limit_brl ||
      0
    );

  if (limit <= 0) {
    return 0;
  }

  return Math.min(
    100,
    (
      Number(
        companyUsage.value.month?.cost_brl ||
        0
      ) /
      limit
    ) * 100
  );
});

const monthlyLimitValue = computed(() => {
  return Number(
    aiSettings.value.monthly_cost_limit_brl ||
    0
  );
});

const monthlyUsageValue = computed(() => {
  return Number(
    companyUsage.value.month?.cost_brl ||
    0
  );
});

const isMonthlyLimitReached = computed(() => {
  return (
    monthlyLimitValue.value > 0 &&
    monthlyUsageValue.value >=
      monthlyLimitValue.value
  );
});

const aiOperationalStatus = computed(() => {
  if (!aiSettings.value.enabled) {
    return 'disabled';
  }

  if (
    isMonthlyLimitReached.value &&
    aiSettings.value.monthly_limit_action ===
      'pause'
  ) {
    return 'paused_limit';
  }

  if (isMonthlyLimitReached.value) {
    return 'limit_alert';
  }

  return 'active';
});

const aiOperationalLabel = computed(() => {
  switch (aiOperationalStatus.value) {
    case 'paused_limit':
      return 'IA pausada por limite mensal';

    case 'limit_alert':
      return 'IA ativa — limite mensal atingido';

    case 'disabled':
      return 'IA desativada';

    default:
      return 'IA ativa';
  }
});

const aiOperationalDescription = computed(() => {
  switch (aiOperationalStatus.value) {
    case 'paused_limit':
      return (
        'Novas chamadas de IA estão bloqueadas ' +
        'porque esta empresa atingiu o limite mensal.'
      );

    case 'limit_alert':
      return (
        'O limite mensal foi atingido, mas a IA ' +
        'continua funcionando porque está configurada ' +
        'como somente aviso.'
      );

    case 'disabled':
      return (
        'A inteligência artificial está desativada ' +
        'para esta empresa.'
      );

    default:
      return (
        'A inteligência artificial está disponível ' +
        'para responder nas Inboxes selecionadas.'
      );
  }
});

const monthlyLimitRemaining = computed(() => {
  const limit =
    Number(
      aiSettings.value.monthly_cost_limit_brl ||
      0
    );

  const used =
    Number(
      companyUsage.value.month?.cost_brl ||
      0
    );

  return Math.max(0, limit - used);
});

const formatNumber = value => {
  return Number(value || 0).toLocaleString(
    'pt-BR'
  );
};

const loadCompanyUsage = async () => {
  usageLoading.value = true;

  try {
    const [
      usageResponse,
      localResponse,
    ] = await Promise.all([
      AiBotSettingsAPI
        .getUsageSummaryForAccount(
          companyId.value
        ),
      AiBotSettingsAPI
        .getLocalUsageSummaryForAccount(
          companyId.value
        ),
    ]);

    companyUsage.value = {
      ...companyUsage.value,
      ...(usageResponse.data || {}),
    };

    localUsage.value = {
      ...localUsage.value,
      ...(localResponse.data || {}),
    };
  } catch (err) {
    console.error(
      'Erro ao carregar consumo da IA:',
      err
    );
  } finally {
    usageLoading.value = false;
  }
};

const loadAiManagement = async () => {
  aiError.value = '';

  try {
    const [
      settingsResponse,
      inboxesResponse,
      teamsResponse,
    ] = await Promise.all([
      AiBotSettingsAPI.getSettingsForAccount(
        companyId.value
      ),
      AiBotSettingsAPI.getInboxesForAccount(
        companyId.value
      ),
      AiBotSettingsAPI.getTeamsForAccount(
        companyId.value
      ),
    ]);

    aiSettings.value = {
      ...aiSettings.value,
      ...(settingsResponse.data || {}),
      allowed_inbox_ids: Array.isArray(
        settingsResponse.data?.allowed_inbox_ids
      )
        ? settingsResponse.data.allowed_inbox_ids
            .map(Number)
            .filter(Boolean)
        : [],
    };

    ensureCompanyVariables();

    companyInboxes.value =
      inboxesResponse.data?.payload ||
      inboxesResponse.data ||
      [];

    companyTeams.value =
      teamsResponse.data?.payload ||
      teamsResponse.data ||
      [];
  } catch (err) {
    console.error(
      'Erro ao carregar gerenciamento da IA:',
      err
    );

    aiError.value =
      'Não foi possível carregar as configurações da IA.';
  }
};

const applyUsageProfile = profile => {
  aiSettings.value.usage_profile = profile;

  if (profile === 'economy') {
    aiSettings.value.history_limit = 6;
    aiSettings.value.max_output_tokens = 300;
    return;
  }

  if (profile === 'balanced') {
    aiSettings.value.history_limit = 15;
    aiSettings.value.max_output_tokens = 700;
    return;
  }

  if (profile === 'advanced') {
    aiSettings.value.history_limit = 30;
    aiSettings.value.max_output_tokens = 1000;
  }
};

const markUsageCustom = () => {
  aiSettings.value.usage_profile = 'custom';
};

const localReplyTemplates = {
  isp: {
    name: 'ISP / Telecom',
    rules: {
      greeting: {
        name: 'Saudações',
        enabled: true,
        phrases: [
          'oi',
          'ola',
          'olá',
          'oie',
          'bom dia',
          'boa tarde',
          'boa noite',
        ],
        match_mode: 'exact',
        reply:
          '{saudacao}! Eu sou {assistente}. Como posso ajudar você?',
        action: 'reply_only',
        sector: 'geral',
      },

      human_agent: {
        name: 'Atendente humano',
        enabled: true,
        phrases: [
          'atendente',
          'falar com atendente',
          'quero um atendente',
          'atendimento humano',
          'quero falar com alguém',
          'quero falar com uma pessoa',
          'falar com uma pessoa',
        ],
        match_mode: 'contains_any',
        reply: '',
        action: 'transfer_only',
        sector: 'geral',
      },

      commercial: {
        name: 'Contratar internet',
        enabled: true,
        phrases: [
          'quero internet',
          'quero contratar',
          'contratar internet',
          'assinar internet',
          'quero assinar',
          'nova instalação',
          'nova instalacao',
          'instalar internet',
        ],
        match_mode: 'contains_any',
        reply: '',
        action: 'transfer_only',
        sector: 'comercial',
      },

      plans: {
        name: 'Planos e preços',
        enabled: true,
        phrases: [
          'quais os planos',
          'quais planos vocês tem',
          'quais planos voces tem',
          'planos de internet',
          'quanto custa a internet',
          'valor da internet',
          'preço da internet',
          'preco da internet',
          'valor dos planos',
          'tem plano',
        ],
        match_mode: 'contains_any',
        reply:
          'Temos opções de planos para diferentes necessidades. Informe seu endereço ou CEP para verificarmos disponibilidade e condições.',
        action: 'reply_only',
        sector: 'comercial',
      },

      coverage: {
        name: 'Cobertura',
        enabled: true,
        phrases: [
          'tem cobertura',
          'tem internet no meu endereço',
          'tem internet no meu endereco',
          'atende meu bairro',
          'vocês atendem meu bairro',
          'voces atendem meu bairro',
          'tem disponibilidade',
          'tem internet aqui',
        ],
        match_mode: 'contains_any',
        reply:
          'Consigo verificar a disponibilidade. Envie seu endereço completo ou CEP.',
        action: 'reply_only',
        sector: 'comercial',
      },

      second_copy: {
        name: '2ª via / Boleto',
        enabled: true,
        phrases: [
          'segunda via',
          'segunda via do boleto',
          '2 via',
          '2ª via',
          'quero meu boleto',
          'boleto',
          'emitir boleto',
          'mandar boleto',
          'enviar boleto',
        ],
        match_mode: 'contains_any',
        reply: '',
        action: 'transfer_only',
        sector: 'financeiro',
      },

      invoice: {
        name: 'Fatura',
        enabled: true,
        phrases: [
          'fatura',
          'minha fatura',
          'ver minha fatura',
          'consultar fatura',
          'conta',
          'valor da fatura',
          'valor da conta',
        ],
        match_mode: 'contains_any',
        reply: '',
        action: 'transfer_only',
        sector: 'financeiro',
      },

      payment: {
        name: 'Pagamento',
        enabled: true,
        phrases: [
          'pagamento',
          'pagar boleto',
          'pagar fatura',
          'pix para pagar',
          'pix pra pagar',
          'como pagar',
          'forma de pagamento',
        ],
        match_mode: 'contains_any',
        reply: '',
        action: 'transfer_only',
        sector: 'financeiro',
      },

      payment_done: {
        name: 'Pagamento realizado',
        enabled: true,
        phrases: [
          'já paguei',
          'ja paguei',
          'paguei o boleto',
          'paguei a fatura',
          'pagamento realizado',
          'já fiz o pagamento',
          'ja fiz o pagamento',
          'comprovante de pagamento',
        ],
        match_mode: 'contains_any',
        reply: '',
        action: 'transfer_only',
        sector: 'financeiro',
      },

      due_date: {
        name: 'Alterar vencimento',
        enabled: true,
        phrases: [
          'alterar vencimento',
          'mudar vencimento',
          'trocar vencimento',
          'mudar data do boleto',
          'trocar data do boleto',
          'data de vencimento',
        ],
        match_mode: 'contains_any',
        reply: '',
        action: 'transfer_only',
        sector: 'financeiro',
      },

      no_internet: {
        name: 'Sem internet',
        enabled: true,
        phrases: [
          'sem internet',
          'estou sem internet',
          'internet caiu',
          'internet não funciona',
          'internet nao funciona',
          'sem conexão',
          'sem conexao',
          'não tenho internet',
          'nao tenho internet',
        ],
        match_mode: 'contains_any',
        reply: '',
        action: 'transfer_only',
        sector: 'suporte',
      },

      slow_internet: {
        name: 'Internet lenta',
        enabled: true,
        phrases: [
          'internet lenta',
          'internet está lenta',
          'internet esta lenta',
          'lentidão',
          'lentidao',
          'internet devagar',
          'velocidade baixa',
          'internet ruim',
        ],
        match_mode: 'contains_any',
        reply: '',
        action: 'transfer_only',
        sector: 'suporte',
      },

      wifi: {
        name: 'Problema no Wi-Fi',
        enabled: true,
        phrases: [
          'wifi não funciona',
          'wifi nao funciona',
          'wi-fi não funciona',
          'wi-fi nao funciona',
          'problema no wifi',
          'problema no wi-fi',
          'wifi caiu',
          'wifi fraco',
          'sinal wifi fraco',
        ],
        match_mode: 'contains_any',
        reply: '',
        action: 'transfer_only',
        sector: 'suporte',
      },

      support: {
        name: 'Suporte técnico',
        enabled: true,
        phrases: [
          'suporte',
          'suporte técnico',
          'suporte tecnico',
          'problema com internet',
          'problema na internet',
          'preciso de suporte',
          'abrir chamado',
        ],
        match_mode: 'contains_any',
        reply: '',
        action: 'transfer_only',
        sector: 'suporte',
      },

      address_change: {
        name: 'Mudança de endereço',
        enabled: true,
        phrases: [
          'mudança de endereço',
          'mudanca de endereco',
          'mudar endereço',
          'mudar endereco',
          'trocar endereço',
          'trocar endereco',
          'transferir internet',
          'levar internet para outro endereço',
          'levar internet para outro endereco',
        ],
        match_mode: 'contains_any',
        reply: '',
        action: 'transfer_only',
        sector: 'comercial',
      },

      upgrade: {
        name: 'Upgrade de plano',
        enabled: true,
        phrases: [
          'upgrade',
          'aumentar plano',
          'aumentar velocidade',
          'quero mais velocidade',
          'mudar meu plano',
          'trocar meu plano',
          'plano maior',
        ],
        match_mode: 'contains_any',
        reply: '',
        action: 'transfer_only',
        sector: 'comercial',
      },

      cancellation: {
        name: 'Cancelamento',
        enabled: true,
        phrases: [
          'quero cancelar',
          'cancelar internet',
          'cancelar meu plano',
          'cancelar contrato',
          'pedido de cancelamento',
          'solicitar cancelamento',
          'encerrar contrato',
        ],
        match_mode: 'contains_any',
        reply: '',
        action: 'transfer_only',
        sector: 'cancelamento',
      },

      business_hours: {
        name: 'Horário de atendimento',
        enabled: true,
        phrases: [
          'qual o horário',
          'qual o horario',
          'horário de atendimento',
          'horario de atendimento',
          'que horas vocês abrem',
          'que horas voces abrem',
          'que horas fecha',
        ],
        match_mode: 'contains_any',
        reply:
          'Nosso horário de atendimento é {horario_atendimento}.',
        action: 'reply_only',
        sector: 'geral',
      },

      address: {
        name: 'Endereço da empresa',
        enabled: true,
        phrases: [
          'qual o endereço',
          'qual o endereco',
          'endereço da empresa',
          'endereco da empresa',
          'onde fica',
          'localização',
          'localizacao',
        ],
        match_mode: 'contains_any',
        reply:
          'Nosso endereço é {endereco_empresa}.',
        action: 'reply_only',
        sector: 'geral',
      },

      thanks: {
        name: 'Agradecimentos',
        enabled: true,
        phrases: [
          'obrigado',
          'obrigada',
          'muito obrigado',
          'muito obrigada',
          'valeu',
        ],
        match_mode: 'exact',
        reply:
          'Por nada! Se precisar de mais alguma coisa, estou à disposição.',
        action: 'reply_only',
        sector: 'geral',
      },

      farewell: {
        name: 'Despedidas',
        enabled: true,
        phrases: [
          'tchau',
          'até mais',
          'ate mais',
          'até logo',
          'ate logo',
        ],
        match_mode: 'exact',
        reply:
          'Até mais! Foi um prazer ajudar você.',
        action: 'reply_only',
        sector: 'geral',
      },
    },
  },

  clinic: {
    name: 'Clínica / Consultório',
    rules: {
      greeting: {
        name: 'Saudações',
        enabled: true,
        phrases: [
          'oi',
          'ola',
          'olá',
          'bom dia',
          'boa tarde',
          'boa noite',
        ],
        match_mode: 'exact',
        reply:
          '{saudacao}! Eu sou {assistente}. Como posso ajudar você?',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },

      human_agent: {
        name: 'Atendente humano',
        enabled: true,
        phrases: [
          'quero falar com atendente',
          'falar com atendente',
          'atendimento humano',
          'quero falar com uma pessoa',
          'falar com uma pessoa',
          'preciso de um atendente',
        ],
        match_mode: 'contains_any',
        reply: '',
        action: 'transfer_only',
        sector: 'geral',
        team_id: null,
      },

      appointment: {
        name: 'Agendamento',
        enabled: true,
        phrases: [
          'quero marcar consulta',
          'marcar consulta',
          'agendar consulta',
          'quero agendar',
          'marcar horário',
          'marcar horario',
          'agendar horário',
          'agendar horario',
          'tem horário disponível',
          'tem horario disponivel',
        ],
        match_mode: 'contains_any',
        reply:
          'Claro. Informe seu nome e qual consulta, exame ou especialidade você deseja agendar.',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },

      reschedule: {
        name: 'Remarcar atendimento',
        enabled: true,
        phrases: [
          'remarcar consulta',
          'mudar consulta',
          'trocar horário da consulta',
          'trocar horario da consulta',
          'alterar horário',
          'alterar horario',
          'mudar horário',
          'mudar horario',
        ],
        match_mode: 'contains_any',
        reply:
          'Posso ajudar com a remarcação. Informe seu nome e a data do atendimento atual.',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },

      cancel_appointment: {
        name: 'Cancelar consulta',
        enabled: true,
        phrases: [
          'cancelar consulta',
          'quero cancelar consulta',
          'cancelar meu horário',
          'cancelar meu horario',
          'desmarcar consulta',
          'desmarcar horário',
          'desmarcar horario',
        ],
        match_mode: 'contains_any',
        reply:
          'Certo. Informe seu nome e a data do atendimento que deseja cancelar.',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },

      prices: {
        name: 'Valores',
        enabled: true,
        phrases: [
          'qual o valor da consulta',
          'valor da consulta',
          'quanto custa a consulta',
          'preço da consulta',
          'preco da consulta',
          'valor do exame',
          'quanto custa o exame',
          'qual o valor',
        ],
        match_mode: 'contains_any',
        reply:
          'Os valores dependem do atendimento ou especialidade. Informe qual serviço você deseja consultar.',
        action: 'reply_only',
        sector: 'comercial',
        team_id: null,
      },

      insurance: {
        name: 'Convênios',
        enabled: true,
        phrases: [
          'aceita convênio',
          'aceita convenio',
          'quais convênios aceita',
          'quais convenios aceita',
          'aceita plano de saúde',
          'aceita plano de saude',
          'atende meu convênio',
          'atende meu convenio',
        ],
        match_mode: 'contains_any',
        reply:
          'Posso verificar. Informe o nome do seu convênio ou plano de saúde.',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },

      exams: {
        name: 'Exames',
        enabled: true,
        phrases: [
          'faz exame',
          'realiza exame',
          'quais exames',
          'agendar exame',
          'marcar exame',
          'preciso fazer exame',
        ],
        match_mode: 'contains_any',
        reply:
          'Informe qual exame você precisa para verificarmos disponibilidade e orientações.',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },

      exam_results: {
        name: 'Resultado de exames',
        enabled: true,
        phrases: [
          'resultado do exame',
          'resultado de exame',
          'meu resultado',
          'pegar resultado',
          'consultar resultado',
          'resultado está pronto',
          'resultado esta pronto',
        ],
        match_mode: 'contains_any',
        reply:
          'Para consultar o resultado, informe seu nome e qual exame foi realizado.',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },

      business_hours: {
        name: 'Horário de atendimento',
        enabled: true,
        phrases: [
          'qual o horário',
          'qual o horario',
          'horário de atendimento',
          'horario de atendimento',
          'que horas abre',
          'que horas fecha',
          'funciona hoje',
        ],
        match_mode: 'contains_any',
        reply:
          'Nosso horário de atendimento é {horario_atendimento}.',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },

      address: {
        name: 'Endereço',
        enabled: true,
        phrases: [
          'qual o endereço',
          'qual o endereco',
          'onde fica',
          'localização',
          'localizacao',
          'como chegar',
        ],
        match_mode: 'contains_any',
        reply:
          'Nosso endereço é {endereco_empresa}.',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },

      thanks: {
        name: 'Agradecimentos',
        enabled: true,
        phrases: [
          'obrigado',
          'obrigada',
          'valeu',
          'muito obrigado',
          'muito obrigada',
        ],
        match_mode: 'exact',
        reply:
          'Por nada! Se precisar de mais alguma coisa, estou à disposição.',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },

      farewell: {
        name: 'Despedidas',
        enabled: true,
        phrases: [
          'tchau',
          'até mais',
          'ate mais',
          'até logo',
          'ate logo',
        ],
        match_mode: 'exact',
        reply:
          'Até mais! Foi um prazer ajudar você.',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },
    },
  },

  retail: {
    name: 'Loja / E-commerce',
    rules: {
      greeting: {
        name: 'Saudações',
        enabled: true,
        phrases: [
          'oi',
          'ola',
          'olá',
          'bom dia',
          'boa tarde',
          'boa noite',
        ],
        match_mode: 'exact',
        reply:
          '{saudacao}! Eu sou {assistente}. Como posso ajudar você?',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },

      human_agent: {
        name: 'Atendente humano',
        enabled: true,
        phrases: [
          'falar com atendente',
          'quero um atendente',
          'atendimento humano',
          'quero falar com uma pessoa',
          'falar com uma pessoa',
        ],
        match_mode: 'contains_any',
        reply: '',
        action: 'transfer_only',
        sector: 'geral',
        team_id: null,
      },

      products: {
        name: 'Produtos',
        enabled: true,
        phrases: [
          'tem esse produto',
          'vocês têm esse produto',
          'voces tem esse produto',
          'quero comprar',
          'procuro um produto',
          'quais produtos vocês vendem',
          'quais produtos voces vendem',
        ],
        match_mode: 'contains_any',
        reply:
          'Posso verificar para você. Informe o nome, modelo ou descrição do produto que procura.',
        action: 'reply_only',
        sector: 'comercial',
        team_id: null,
      },

      prices: {
        name: 'Preços e promoções',
        enabled: true,
        phrases: [
          'qual o preço',
          'qual o preco',
          'quanto custa',
          'qual o valor',
          'tem promoção',
          'tem promocao',
          'está em promoção',
          'esta em promocao',
        ],
        match_mode: 'contains_any',
        reply:
          'Informe qual produto você procura para verificarmos valor e promoções disponíveis.',
        action: 'reply_only',
        sector: 'comercial',
        team_id: null,
      },

      stock: {
        name: 'Estoque',
        enabled: true,
        phrases: [
          'tem em estoque',
          'tem estoque',
          'está disponível',
          'esta disponivel',
          'tem disponível',
          'tem disponivel',
          'ainda tem',
        ],
        match_mode: 'contains_any',
        reply:
          'Informe o produto, modelo, tamanho ou variação para verificarmos a disponibilidade.',
        action: 'reply_only',
        sector: 'comercial',
        team_id: null,
      },

      order_status: {
        name: 'Status do pedido',
        enabled: true,
        phrases: [
          'meu pedido',
          'status do pedido',
          'onde está meu pedido',
          'onde esta meu pedido',
          'acompanhar pedido',
          'consultar pedido',
          'pedido atrasado',
        ],
        match_mode: 'contains_any',
        reply:
          'Informe o número do pedido ou os dados utilizados na compra para verificarmos.',
        action: 'reply_only',
        sector: 'suporte',
        team_id: null,
      },

      tracking: {
        name: 'Rastreamento',
        enabled: true,
        phrases: [
          'rastrear pedido',
          'código de rastreio',
          'codigo de rastreio',
          'rastreamento',
          'rastrear entrega',
          'onde está minha entrega',
          'onde esta minha entrega',
        ],
        match_mode: 'contains_any',
        reply:
          'Informe o número do pedido para verificarmos as informações de rastreamento.',
        action: 'reply_only',
        sector: 'suporte',
        team_id: null,
      },

      delivery: {
        name: 'Entrega',
        enabled: true,
        phrases: [
          'vocês entregam',
          'voces entregam',
          'faz entrega',
          'qual o prazo de entrega',
          'quanto custa a entrega',
          'taxa de entrega',
          'frete',
        ],
        match_mode: 'contains_any',
        reply:
          'Informe seu CEP para verificarmos disponibilidade, prazo e condições de entrega.',
        action: 'reply_only',
        sector: 'comercial',
        team_id: null,
      },

      exchange_return: {
        name: 'Troca / Devolução',
        enabled: true,
        phrases: [
          'quero trocar',
          'trocar produto',
          'fazer troca',
          'quero devolver',
          'devolver produto',
          'devolução',
          'devolucao',
          'produto com defeito',
        ],
        match_mode: 'contains_any',
        reply:
          'Informe o número do pedido e o motivo da troca ou devolução para iniciarmos o atendimento.',
        action: 'reply_only',
        sector: 'suporte',
        team_id: null,
      },

      payment: {
        name: 'Pagamento',
        enabled: true,
        phrases: [
          'forma de pagamento',
          'formas de pagamento',
          'como pagar',
          'aceita pix',
          'aceita cartão',
          'aceita cartao',
          'boleto',
          'pagamento',
        ],
        match_mode: 'contains_any',
        reply:
          'Posso ajudar com as formas de pagamento. Informe o produto ou pedido relacionado.',
        action: 'reply_only',
        sector: 'financeiro',
        team_id: null,
      },

      cancel_order: {
        name: 'Cancelar pedido',
        enabled: true,
        phrases: [
          'cancelar pedido',
          'quero cancelar pedido',
          'cancelar minha compra',
          'desistir da compra',
        ],
        match_mode: 'contains_any',
        reply:
          'Informe o número do pedido para verificarmos a possibilidade de cancelamento.',
        action: 'reply_only',
        sector: 'cancelamento',
        team_id: null,
      },

      business_hours: {
        name: 'Horário de atendimento',
        enabled: true,
        phrases: [
          'qual o horário',
          'qual o horario',
          'horário de atendimento',
          'horario de atendimento',
          'que horas abre',
          'que horas fecha',
        ],
        match_mode: 'contains_any',
        reply:
          'Nosso horário de atendimento é {horario_atendimento}.',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },

      address: {
        name: 'Endereço',
        enabled: true,
        phrases: [
          'qual o endereço',
          'qual o endereco',
          'onde fica a loja',
          'localização da loja',
          'localizacao da loja',
          'loja física',
          'loja fisica',
        ],
        match_mode: 'contains_any',
        reply:
          'Nosso endereço é {endereco_empresa}.',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },

      thanks: {
        name: 'Agradecimentos',
        enabled: true,
        phrases: [
          'obrigado',
          'obrigada',
          'valeu',
        ],
        match_mode: 'exact',
        reply:
          'Por nada! Se precisar de mais alguma coisa, estou à disposição.',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },

      farewell: {
        name: 'Despedidas',
        enabled: true,
        phrases: [
          'tchau',
          'até mais',
          'ate mais',
          'até logo',
          'ate logo',
        ],
        match_mode: 'exact',
        reply:
          'Até mais! Foi um prazer ajudar você.',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },
    },
  },

  real_estate: {
    name: 'Imobiliária',
    rules: {
      greeting: {
        name: 'Saudações',
        enabled: true,
        phrases: [
          'oi',
          'ola',
          'olá',
          'bom dia',
          'boa tarde',
          'boa noite',
        ],
        match_mode: 'exact',
        reply:
          '{saudacao}! Eu sou {assistente}. Como posso ajudar você?',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },

      human_agent: {
        name: 'Atendente humano',
        enabled: true,
        phrases: [
          'falar com atendente',
          'quero um atendente',
          'quero falar com corretor',
          'falar com corretor',
          'falar com uma pessoa',
        ],
        match_mode: 'contains_any',
        reply: '',
        action: 'transfer_only',
        sector: 'geral',
        team_id: null,
      },

      buy_property: {
        name: 'Comprar imóvel',
        enabled: true,
        phrases: [
          'quero comprar imóvel',
          'quero comprar imovel',
          'comprar apartamento',
          'comprar casa',
          'imóvel para comprar',
          'imovel para comprar',
          'procuro imóvel',
          'procuro imovel',
        ],
        match_mode: 'contains_any',
        reply:
          'Posso ajudar. Informe a região, tipo de imóvel e faixa de valor que procura.',
        action: 'reply_only',
        sector: 'comercial',
        team_id: null,
      },

      rent_property: {
        name: 'Alugar imóvel',
        enabled: true,
        phrases: [
          'quero alugar',
          'alugar apartamento',
          'alugar casa',
          'imóvel para alugar',
          'imovel para alugar',
          'procuro aluguel',
          'apartamento para aluguel',
          'casa para aluguel',
        ],
        match_mode: 'contains_any',
        reply:
          'Informe a região, tipo de imóvel e faixa de valor que você procura para locação.',
        action: 'reply_only',
        sector: 'comercial',
        team_id: null,
      },

      available_properties: {
        name: 'Imóveis disponíveis',
        enabled: true,
        phrases: [
          'imóveis disponíveis',
          'imoveis disponiveis',
          'tem apartamento',
          'tem casa',
          'quais imóveis',
          'quais imoveis',
          'imóveis para venda',
          'imoveis para venda',
        ],
        match_mode: 'contains_any',
        reply:
          'Informe a região, tipo de imóvel e se deseja comprar ou alugar.',
        action: 'reply_only',
        sector: 'comercial',
        team_id: null,
      },

      schedule_visit: {
        name: 'Agendar visita',
        enabled: true,
        phrases: [
          'agendar visita',
          'marcar visita',
          'visitar imóvel',
          'visitar imovel',
          'quero conhecer o imóvel',
          'quero conhecer o imovel',
          'ver apartamento',
          'ver casa',
        ],
        match_mode: 'contains_any',
        reply:
          'Claro. Informe qual imóvel deseja visitar e o melhor dia e horário para você.',
        action: 'reply_only',
        sector: 'comercial',
        team_id: null,
      },

      financing: {
        name: 'Financiamento',
        enabled: true,
        phrases: [
          'financiamento',
          'financiar imóvel',
          'financiar imovel',
          'simular financiamento',
          'entrada do imóvel',
          'entrada do imovel',
          'parcela do financiamento',
        ],
        match_mode: 'contains_any',
        reply:
          'Podemos orientar sobre financiamento. Informe o imóvel ou faixa de valor que pretende financiar.',
        action: 'reply_only',
        sector: 'financeiro',
        team_id: null,
      },

      documentation: {
        name: 'Documentação',
        enabled: true,
        phrases: [
          'documentos necessários',
          'documentos necessarios',
          'qual documentação',
          'qual documentacao',
          'documentos para alugar',
          'documentos para comprar',
          'documentação do imóvel',
          'documentacao do imovel',
        ],
        match_mode: 'contains_any',
        reply:
          'Informe se o atendimento é para compra, venda ou locação para orientarmos sobre a documentação.',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },

      owner: {
        name: 'Proprietário / Anunciar imóvel',
        enabled: true,
        phrases: [
          'quero anunciar meu imóvel',
          'quero anunciar meu imovel',
          'quero colocar para alugar',
          'quero vender meu imóvel',
          'quero vender meu imovel',
          'sou proprietário',
          'sou proprietario',
          'cadastrar imóvel',
          'cadastrar imovel',
        ],
        match_mode: 'contains_any',
        reply:
          'Ótimo. Informe seu nome, tipo do imóvel e localização para iniciarmos o atendimento.',
        action: 'reply_only',
        sector: 'comercial',
        team_id: null,
      },

      maintenance: {
        name: 'Manutenção / Problema no imóvel',
        enabled: true,
        phrases: [
          'problema no imóvel',
          'problema no imovel',
          'preciso de manutenção',
          'preciso de manutencao',
          'reparo no imóvel',
          'reparo no imovel',
          'problema no apartamento',
          'problema na casa',
        ],
        match_mode: 'contains_any',
        reply:
          'Informe seu nome, imóvel e uma breve descrição do problema para encaminharmos o atendimento.',
        action: 'reply_only',
        sector: 'suporte',
        team_id: null,
      },

      business_hours: {
        name: 'Horário de atendimento',
        enabled: true,
        phrases: [
          'qual o horário',
          'qual o horario',
          'horário de atendimento',
          'horario de atendimento',
          'que horas abre',
          'que horas fecha',
        ],
        match_mode: 'contains_any',
        reply:
          'Nosso horário de atendimento é {horario_atendimento}.',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },

      address: {
        name: 'Endereço',
        enabled: true,
        phrases: [
          'qual o endereço',
          'qual o endereco',
          'onde fica a imobiliária',
          'onde fica a imobiliaria',
          'localização',
          'localizacao',
        ],
        match_mode: 'contains_any',
        reply:
          'Nosso endereço é {endereco_empresa}.',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },

      thanks: {
        name: 'Agradecimentos',
        enabled: true,
        phrases: [
          'obrigado',
          'obrigada',
          'valeu',
        ],
        match_mode: 'exact',
        reply:
          'Por nada! Se precisar de mais alguma coisa, estou à disposição.',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },

      farewell: {
        name: 'Despedidas',
        enabled: true,
        phrases: [
          'tchau',
          'até mais',
          'ate mais',
          'até logo',
          'ate logo',
        ],
        match_mode: 'exact',
        reply:
          'Até mais! Foi um prazer ajudar você.',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },
    },
  },

  restaurant: {
    name: 'Restaurante / Delivery',
    rules: {
      greeting: {
        name: 'Saudações',
        enabled: true,
        phrases: [
          'oi',
          'ola',
          'olá',
          'bom dia',
          'boa tarde',
          'boa noite',
        ],
        match_mode: 'exact',
        reply:
          '{saudacao}! Eu sou {assistente}. Como posso ajudar você?',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },

      human_agent: {
        name: 'Atendente humano',
        enabled: true,
        phrases: [
          'falar com atendente',
          'quero um atendente',
          'falar com uma pessoa',
          'atendimento humano',
        ],
        match_mode: 'contains_any',
        reply: '',
        action: 'transfer_only',
        sector: 'geral',
        team_id: null,
      },

      menu: {
        name: 'Cardápio',
        enabled: true,
        phrases: [
          'qual o cardápio',
          'qual o cardapio',
          'manda o cardápio',
          'manda o cardapio',
          'quero ver o cardápio',
          'quero ver o cardapio',
          'ver menu',
          'menu',
        ],
        match_mode: 'contains_any',
        reply:
          'Posso ajudar com o cardápio. Informe se procura pratos, bebidas, combos ou promoções.',
        action: 'reply_only',
        sector: 'comercial',
        team_id: null,
      },

      prices: {
        name: 'Preços',
        enabled: true,
        phrases: [
          'qual o preço',
          'qual o preco',
          'quanto custa',
          'qual o valor',
          'preço do prato',
          'preco do prato',
          'valor do prato',
        ],
        match_mode: 'contains_any',
        reply:
          'Informe qual item do cardápio você deseja consultar.',
        action: 'reply_only',
        sector: 'comercial',
        team_id: null,
      },

      delivery: {
        name: 'Delivery / Entrega',
        enabled: true,
        phrases: [
          'faz delivery',
          'vocês entregam',
          'voces entregam',
          'faz entrega',
          'tem delivery',
          'entrega aqui',
        ],
        match_mode: 'contains_any',
        reply:
          'Informe seu endereço ou CEP para verificarmos a disponibilidade de entrega.',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },

      delivery_fee: {
        name: 'Taxa de entrega',
        enabled: true,
        phrases: [
          'taxa de entrega',
          'qual a taxa',
          'quanto é a entrega',
          'quanto e a entrega',
          'valor da entrega',
          'frete',
        ],
        match_mode: 'contains_any',
        reply:
          'Informe seu endereço ou CEP para verificarmos a taxa de entrega.',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },

      order: {
        name: 'Fazer pedido',
        enabled: true,
        phrases: [
          'quero pedir',
          'fazer pedido',
          'quero fazer um pedido',
          'pedir comida',
          'quero comprar',
          'quero pedir delivery',
        ],
        match_mode: 'contains_any',
        reply:
          'Claro. Informe os itens que deseja pedir e se será entrega ou retirada.',
        action: 'reply_only',
        sector: 'comercial',
        team_id: null,
      },

      order_status: {
        name: 'Status do pedido',
        enabled: true,
        phrases: [
          'cadê meu pedido',
          'cade meu pedido',
          'meu pedido está atrasado',
          'meu pedido esta atrasado',
          'onde está meu pedido',
          'onde esta meu pedido',
          'status do pedido',
        ],
        match_mode: 'contains_any',
        reply:
          'Informe o nome ou número do pedido para verificarmos o andamento.',
        action: 'reply_only',
        sector: 'suporte',
        team_id: null,
      },

      reservation: {
        name: 'Reservas',
        enabled: true,
        phrases: [
          'quero reservar',
          'fazer reserva',
          'reservar mesa',
          'quero reservar uma mesa',
          'tem mesa',
          'reserva para hoje',
        ],
        match_mode: 'contains_any',
        reply:
          'Informe a data, horário e quantidade de pessoas para solicitar sua reserva.',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },

      payment: {
        name: 'Formas de pagamento',
        enabled: true,
        phrases: [
          'forma de pagamento',
          'formas de pagamento',
          'aceita pix',
          'aceita cartão',
          'aceita cartao',
          'paga na entrega',
          'como pagar',
        ],
        match_mode: 'contains_any',
        reply:
          'Posso ajudar com as formas de pagamento disponíveis para seu pedido.',
        action: 'reply_only',
        sector: 'financeiro',
        team_id: null,
      },

      cancel_order: {
        name: 'Cancelar pedido / Reserva',
        enabled: true,
        phrases: [
          'cancelar pedido',
          'quero cancelar pedido',
          'cancelar reserva',
          'quero cancelar reserva',
          'desmarcar reserva',
        ],
        match_mode: 'contains_any',
        reply:
          'Informe o nome ou número do pedido/reserva para verificarmos o cancelamento.',
        action: 'reply_only',
        sector: 'cancelamento',
        team_id: null,
      },

      business_hours: {
        name: 'Horário de funcionamento',
        enabled: true,
        phrases: [
          'qual o horário',
          'qual o horario',
          'horário de funcionamento',
          'horario de funcionamento',
          'que horas abre',
          'que horas fecha',
          'está aberto',
          'esta aberto',
        ],
        match_mode: 'contains_any',
        reply:
          'Nosso horário de funcionamento é {horario_atendimento}.',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },

      address: {
        name: 'Endereço',
        enabled: true,
        phrases: [
          'qual o endereço',
          'qual o endereco',
          'onde fica',
          'localização',
          'localizacao',
          'como chegar',
        ],
        match_mode: 'contains_any',
        reply:
          'Nosso endereço é {endereco_empresa}.',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },

      thanks: {
        name: 'Agradecimentos',
        enabled: true,
        phrases: [
          'obrigado',
          'obrigada',
          'valeu',
        ],
        match_mode: 'exact',
        reply:
          'Por nada! Se precisar de mais alguma coisa, estou à disposição.',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },

      farewell: {
        name: 'Despedidas',
        enabled: true,
        phrases: [
          'tchau',
          'até mais',
          'ate mais',
          'até logo',
          'ate logo',
        ],
        match_mode: 'exact',
        reply:
          'Até mais! Foi um prazer ajudar você.',
        action: 'reply_only',
        sector: 'geral',
        team_id: null,
      },
    },
  },

};

const localReplyTemplateOptions = computed(() =>
  Object.entries(localReplyTemplates).map(
    ([key, template]) => ({
      key,
      name: template.name,
    })
  )
);

const cloneLocalReplyRules = rules =>
  JSON.parse(JSON.stringify(rules || {}));

const applyLocalReplyTemplate = () => {
  aiError.value = '';
  aiSuccess.value = '';

  const template =
    localReplyTemplates[
      selectedLocalTemplate.value
    ];

  if (!template) {
    aiError.value =
      'Selecione um modelo de segmento.';
    return;
  }

  const templateRules =
    cloneLocalReplyRules(template.rules);

  const currentRules =
    cloneLocalReplyRules(
      aiSettings.value.local_reply_rules || {}
    );

  const templateCount =
    Object.keys(templateRules).length;

  if (
    !window.confirm(
      `Adicionar o modelo "${template.name}"?\n\n` +
      `${templateCount} categoria(s) serão analisadas ` +
      'e adicionadas às categorias atuais.\n\n' +
      'Categorias existentes não serão apagadas.\n\n' +
      'Nada será salvo até você clicar em ' +
      '"Salvar configurações da IA".'
    )
  ) {
    return;
  }

  let added = 0;
  let skipped = 0;

  const existingNames = new Set(
    Object.values(currentRules)
      .map(rule =>
        normalizeLocalPhrase(
          rule?.name || ''
        )
      )
      .filter(Boolean)
  );

  Object.entries(templateRules).forEach(
    ([templateKey, templateRule]) => {
      const normalizedName =
        normalizeLocalPhrase(
          templateRule?.name || templateKey
        );

      if (
        normalizedName &&
        existingNames.has(normalizedName)
      ) {
        skipped += 1;
        return;
      }

      let targetKey =
        `segment_${selectedLocalTemplate.value}_${templateKey}`;

      if (currentRules[targetKey]) {
        targetKey = createLocalRuleKey();
      }

      currentRules[targetKey] = {
        ...templateRule,

        match_mode:
          templateRule.match_mode ||
          'contains_any',

        action:
          templateRule.action ||
          'reply_only',

        sector:
          templateRule.sector ||
          'geral',
      };

      if (normalizedName) {
        existingNames.add(normalizedName);
      }

      added += 1;
    }
  );

  aiSettings.value.local_reply_rules =
    currentRules;

  aiSettings.value.local_replies_enabled = true;

  aiSuccess.value =
    `Modelo "${template.name}" processado: ` +
    `${added} categoria(s) adicionada(s)` +
    (
      skipped
        ? ` e ${skipped} já existente(s) ignorada(s).`
        : '.'
    ) +
    ' Revise e salve quando estiver pronto.';
};


const ensureCompanyVariables = () => {
  const current =
    aiSettings.value.company_variables || {};

  aiSettings.value.company_variables = {
        ...current,
company_name:
      String(
        current.company_name ||
        company.value?.name ||
        ''
      ),
    phone:
      String(current.phone || ''),
    whatsapp:
      String(current.whatsapp || ''),
    address:
      String(current.address || ''),
    business_hours:
      String(current.business_hours || ''),
    site:
      String(current.site || ''),
    email:
      String(current.email || ''),
  };
};

const localRuleLabels = {
  greeting: 'Saudações',
  thanks: 'Agradecimentos',
  farewell: 'Despedidas',
};

const normalizeLocalPhrase = value =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[!?.,;:]+$/g, '')
    .replace(/\s+/g, ' ');

const localPhraseConflicts = computed(() => {
  const rules =
    aiSettings.value.local_reply_rules || {};

  const phrases = new Map();

  Object.entries(rules).forEach(([key, rule]) => {
    if (!rule || !Array.isArray(rule.phrases)) {
      return;
    }

    rule.phrases.forEach(originalPhrase => {
      const normalized =
        normalizeLocalPhrase(originalPhrase);

      if (!normalized) {
        return;
      }

      if (!phrases.has(normalized)) {
        phrases.set(normalized, {
          phrase: String(originalPhrase).trim(),
          categories: new Map(),
        });
      }

      const item = phrases.get(normalized);

      item.categories.set(
        key,
        localRuleTitle(key, rule)
      );
    });
  });

  return Array.from(phrases.values())
    .filter(
      item =>
        item.categories.size > 1
    )
    .map(item => ({
      phrase: item.phrase,
      categories:
        Array.from(item.categories.values()),
    }));
});

const fixedLocalReplyVariables = [
  {
    key: '{empresa}',
    label: 'Empresa',
  },
  {
    key: '{assistente}',
    label: 'Assistente',
  },
  {
    key: '{saudacao}',
    label: 'Saudação',
  },
  {
    key: '{telefone_empresa}',
    label: 'Telefone',
  },
  {
    key: '{whatsapp_empresa}',
    label: 'WhatsApp',
  },
  {
    key: '{endereco_empresa}',
    label: 'Endereço',
  },
  {
    key: '{horario_atendimento}',
    label: 'Horário',
  },
  {
    key: '{site_empresa}',
    label: 'Site',
  },
  {
    key: '{email_empresa}',
    label: 'E-mail',
  },
];

const customVariablePrefix = 'custom__';

const customCompanyVariableEntries = computed(() =>
  Object.entries(
    aiSettings.value.company_variables || {}
  )
    .filter(([key]) =>
      key.startsWith(customVariablePrefix)
    )
    .map(([storageKey, value]) => ({
      storageKey,
      key:
        storageKey.slice(
          customVariablePrefix.length
        ),
      value: String(value || ''),
    }))
    .sort((a, b) =>
      a.key.localeCompare(
        b.key,
        'pt-BR'
      )
    )
);

const localReplyVariables = computed(() => {
  const custom =
    customCompanyVariableEntries.value.map(
      item => ({
        key: `{${item.key}}`,
        label: item.key,
      })
    );

  return [
    ...fixedLocalReplyVariables,
    ...custom,
  ];
});

const reservedCompanyVariableKeys = new Set([
  'empresa',
  'assistente',
  'saudacao',
  'telefone_empresa',
  'whatsapp_empresa',
  'endereco_empresa',
  'horario_atendimento',
  'site_empresa',
  'email_empresa',
]);

const normalizeCustomCompanyVariableKey = value =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');

const createCustomCompanyVariableKey = () => {
  const variables =
    aiSettings.value.company_variables || {};

  let number = 1;
  let key = `nova_variavel_${number}`;

  while (
    Object.prototype.hasOwnProperty.call(
      variables,
      `${customVariablePrefix}${key}`
    )
  ) {
    number += 1;
    key = `nova_variavel_${number}`;
  }

  return key;
};

const addCustomCompanyVariable = () => {
  if (!aiSettings.value.company_variables) {
    aiSettings.value.company_variables = {};
  }

  const key =
    createCustomCompanyVariableKey();

  aiSettings.value.company_variables[
    `${customVariablePrefix}${key}`
  ] = '';
};

const renameCustomCompanyVariable = (
  oldStorageKey,
  rawKey
) => {
  const variables =
    aiSettings.value.company_variables || {};

  const normalized =
    normalizeCustomCompanyVariableKey(
      rawKey
    );

  if (!normalized) {
    window.alert(
      'Informe um nome válido para a variável.'
    );
    return;
  }

  if (
    reservedCompanyVariableKeys.has(
      normalized
    )
  ) {
    window.alert(
      `A variável {${normalized}} já é reservada pelo sistema.`
    );
    return;
  }

  const newStorageKey =
    `${customVariablePrefix}${normalized}`;

  if (newStorageKey === oldStorageKey) {
    return;
  }

  if (
    Object.prototype.hasOwnProperty.call(
      variables,
      newStorageKey
    )
  ) {
    window.alert(
      `A variável {${normalized}} já existe.`
    );
    return;
  }

  const value =
    String(
      variables[oldStorageKey] || ''
    );

  delete variables[oldStorageKey];

  variables[newStorageKey] =
    value;
};

const setCustomCompanyVariableValue = (
  storageKey,
  value
) => {
  if (!aiSettings.value.company_variables) {
    aiSettings.value.company_variables = {};
  }

  aiSettings.value.company_variables[
    storageKey
  ] = String(value || '');
};

const removeCustomCompanyVariable = item => {
  if (!item) return;

  if (
    !window.confirm(
      `Excluir a variável {${item.key}}?`
    )
  ) {
    return;
  }

  delete aiSettings.value.company_variables[
    item.storageKey
  ];
};

const insertLocalReplyVariable = (
  rule,
  variable
) => {
  if (!rule) return;

  const current =
    String(rule.reply || '');

  const separator =
    current &&
    !current.endsWith(' ') &&
    !current.endsWith('\n')
      ? ' '
      : '';

  rule.reply =
    `${current}${separator}${variable}`;
};


const localActionLabels = {
  reply_only: 'Apenas responder',
  reply_and_transfer: 'Responder e transferir',
  transfer_only: 'Transferir sem responder',
};

const localSectorLabels = {
  geral: 'Atendimento geral',
  comercial: 'Comercial',
  financeiro: 'Financeiro',
  suporte: 'Suporte',
  cancelamento: 'Cancelamento',
};

const getLocalSectorTeamId = sector => {
  const normalizedSector =
    String(sector || 'geral')
      .trim()
      .toLowerCase();

  const fieldMap = {
    geral: 'general_team_id',
    comercial: 'commercial_team_id',
    financeiro: 'financial_team_id',
    suporte: 'support_team_id',
    cancelamento: 'cancellation_team_id',
  };

  const field =
    fieldMap[normalizedSector] ||
    'general_team_id';

  const value =
    aiSettings.value?.[field];

  const number = Number(value);

  return Number.isFinite(number) && number > 0
    ? number
    : null;
};

const getLocalSectorTeamName = sector => {
  const teamId =
    getLocalSectorTeamId(sector);

  if (!teamId) {
    return 'Não configurado';
  }

  const team =
    (companyTeams.value || []).find(
      item => Number(item.id) === Number(teamId)
    );

  return (
    team?.name ||
    `Time #${teamId}`
  );
};

const getLocalRuleSpecificTeamId = rule => {
  const number =
    Number(rule?.team_id || 0);

  return Number.isFinite(number) && number > 0
    ? number
    : null;
};

const getLocalRuleDestinationTeamId = rule => {
  const specificTeamId =
    getLocalRuleSpecificTeamId(rule);

  if (specificTeamId) {
    return specificTeamId;
  }

  return getLocalSectorTeamId(
    rule?.sector || 'geral'
  );
};

const getLocalRuleDestinationTeamName = rule => {
  const teamId =
    getLocalRuleDestinationTeamId(rule);

  if (!teamId) {
    return 'Não configurado';
  }

  const team =
    (companyTeams.value || []).find(
      item =>
        Number(item.id) === Number(teamId)
    );

  return (
    team?.name ||
    `Time #${teamId}`
  );
};

const validateLocalTransferRules = () => {
  const rules =
    aiSettings.value.local_reply_rules || {};

  for (
    const [key, rule]
    of Object.entries(rules)
  ) {
    if (!rule || rule.enabled === false) {
      continue;
    }

    const action =
      String(rule.action || 'reply_only')
        .trim()
        .toLowerCase();

    if (
      action !== 'reply_and_transfer' &&
      action !== 'transfer_only'
    ) {
      continue;
    }

    const specificTeamId =
      getLocalRuleSpecificTeamId(rule);

    if (specificTeamId) {
      const teamExists =
        (companyTeams.value || []).some(
          item =>
            Number(item.id) ===
            Number(specificTeamId)
        );

      if (teamExists) {
        continue;
      }

      const categoryName =
        localRuleTitle(key, rule);

      return (
        `A categoria "${categoryName}" está configurada ` +
        `para o time específico #${specificTeamId}, ` +
        'mas esse time não existe nesta empresa.'
      );
    }

    const sector =
      String(rule.sector || 'geral')
        .trim()
        .toLowerCase();

    const teamId =
      getLocalSectorTeamId(sector);

    if (teamId) {
      continue;
    }

    const categoryName =
      localRuleTitle(key, rule);

    const sectorName =
      localSectorLabels[sector] ||
      sector;

    return (
      `A categoria "${categoryName}" está configurada ` +
      `para transferir ao setor "${sectorName}", ` +
      'mas esse setor ainda não possui um time definido.'
    );
  }

  return '';
};

const localReplyTesterMessage = ref('');

const localReplyTesterResult = computed(() => {
  const originalMessage =
    String(localReplyTesterMessage.value || '');

  const normalizedMessage =
    normalizeLocalPhrase(originalMessage);

  if (!normalizedMessage) {
    return {
      status: 'empty',
      categoryKey: '',
      categoryName: '',
      reply: '',
      reason:
        'Digite uma mensagem para testar.',
    };
  }

  if (
    aiSettings.value.local_replies_enabled === false
  ) {
    return {
      status: 'disabled',
      categoryKey: '',
      categoryName: '',
      reply: '',
      reason:
        'As respostas locais estão desativadas.',
    };
  }

  const rules =
    aiSettings.value.local_reply_rules || {};

  for (
    const [key, rule]
    of Object.entries(rules)
  ) {
    if (!rule || rule.enabled === false) {
      continue;
    }

    const phrases =
      Array.isArray(rule.phrases)
        ? rule.phrases
        : [];

    const normalizedPhrases =
      phrases
        .map(normalizeLocalPhrase)
        .filter(Boolean);

    const requestedMatchMode =
      String(rule.match_mode || 'exact')
        .trim()
        .toLowerCase();

    const matchMode =
      requestedMatchMode === 'contains_any'
        ? 'contains_any'
        : 'exact';

    const matched =
      matchMode === 'contains_any'
        ? normalizedPhrases.some(
            phrase =>
              phrase &&
              normalizedMessage.includes(phrase)
          )
        : normalizedPhrases.includes(
            normalizedMessage
          );

    if (!matched) {
      continue;
    }

    let greeting = 'Olá';

    if (normalizedMessage === 'bom dia') {
      greeting = 'Bom dia';
    } else if (
      normalizedMessage === 'boa tarde'
    ) {
      greeting = 'Boa tarde';
    } else if (
      normalizedMessage === 'boa noite'
    ) {
      greeting = 'Boa noite';
    }

    const variables =
      aiSettings.value.company_variables || {};

    const replacements = {
      empresa:
        variables.company_name || '',
      telefone_empresa:
        variables.phone || '',
      whatsapp_empresa:
        variables.whatsapp || '',
      endereco_empresa:
        variables.address || '',
      horario_atendimento:
        variables.business_hours || '',
      site_empresa:
        variables.site || '',
      email_empresa:
        variables.email || '',
      assistente:
        aiSettings.value.assistant_name ||
        'Assistente',
      saudacao:
        greeting,
    };

    for (
      const [storageKey, value]
      of Object.entries(variables)
    ) {
      if (
        !storageKey.startsWith(
          customVariablePrefix
        )
      ) {
        continue;
      }

      const customKey =
        storageKey
          .slice(
            customVariablePrefix.length
          )
          .trim();

      if (!customKey) {
        continue;
      }

      replacements[customKey] =
        String(value || '');
    }

    let reply =
      String(rule.reply || '');

    for (
      const [variableKey, value]
      of Object.entries(replacements)
    ) {
      reply =
        reply.replace(
          new RegExp(
            `\\{${variableKey}\\}`,
            'g'
          ),
          String(value || '')
        );
    }

    reply =
      reply
        .replace(/[ \t]{2,}/g, ' ')
        .trim();

    const action =
      String(rule.action || 'reply_only')
        .trim()
        .toLowerCase();

    const sector =
      String(rule.sector || 'geral')
        .trim()
        .toLowerCase();

    const shouldTransfer =
      action === 'reply_and_transfer' ||
      action === 'transfer_only';

    const teamId =
      shouldTransfer
        ? getLocalRuleDestinationTeamId(rule)
        : null;

    return {
      status: 'local',
      categoryKey: key,
      categoryName:
        localRuleTitle(key, rule),
      reply,
      matchMode,
      matchModeLabel:
        matchMode === 'contains_any'
          ? 'Contém uma das frases'
          : 'Frase exata',
      action,
      actionLabel:
        localActionLabels[action] ||
        localActionLabels.reply_only,
      sector,
      sectorLabel:
        localSectorLabels[sector] ||
        localSectorLabels.geral,
      shouldTransfer,
      teamId,
      teamName:
        shouldTransfer
          ? getLocalRuleDestinationTeamName(rule)
          : '',
      specificTeamId:
        getLocalRuleSpecificTeamId(rule),
      usesAi: false,
      missingVariables:
        getMissingLocalReplyVariables(
          rule.reply
        ),
      reason: '',
    };
  }

  return {
    status: 'ai',
    categoryKey: '',
    categoryName: '',
    reply: '',
    reason:
      'Nenhuma regra local correspondeu à mensagem.',
  };
});


const getLocalReplyVariableValues = () => {
  const variables =
    aiSettings.value.company_variables || {};

  const values = {
    empresa:
      variables.company_name ||
      company.value?.name ||
      '',
    telefone_empresa:
      variables.phone || '',
    whatsapp_empresa:
      variables.whatsapp || '',
    endereco_empresa:
      variables.address || '',
    horario_atendimento:
      variables.business_hours || '',
    site_empresa:
      variables.site || '',
    email_empresa:
      variables.email || '',

    // Sempre possuem fallback operacional.
    assistente:
      aiSettings.value.assistant_name ||
      'Assistente',
    saudacao:
      'Olá',
  };

  for (
    const [storageKey, value]
    of Object.entries(variables)
  ) {
    if (
      !storageKey.startsWith(
        customVariablePrefix
      )
    ) {
      continue;
    }

    const key =
      storageKey
        .slice(
          customVariablePrefix.length
        )
        .trim();

    if (!key) {
      continue;
    }

    values[key] =
      String(value || '');
  }

  return values;
};

const getMissingLocalReplyVariables = text => {
  const content =
    String(text || '');

  if (!content) {
    return [];
  }

  const found = new Set();

  const regex =
    /\{([a-zA-Z0-9_]+)\}/g;

  let match;

  while (
    (match = regex.exec(content)) !== null
  ) {
    if (match[1]) {
      found.add(match[1]);
    }
  }

  if (!found.size) {
    return [];
  }

  const values =
    getLocalReplyVariableValues();

  return Array.from(found)
    .filter(key => {
      if (
        !Object.prototype.hasOwnProperty.call(
          values,
          key
        )
      ) {
        return true;
      }

      return (
        String(values[key] || '')
          .trim() === ''
      );
    })
    .map(key => `{${key}}`);
};


const formatAutomationCurrency = value =>
  Number(value || 0).toLocaleString(
    'pt-BR',
    {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );

const formatAutomationPercent = value =>
  Number(value || 0).toLocaleString(
    'pt-BR',
    {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }
  );


const operationalHealth = computed(() => {
  const provider =
    company.value?.ai?.whatsapp_provider ||
    'evolution';

  const instance =
    company.value?.ai?.evolution_instance_name;

  const evolutionState =
    getEvolutionState(
      evolutionStatus.value
    );

  const whatsappConfigured =
    provider !== 'evolution' ||
    Boolean(instance);

  const whatsappOnline =
    provider === 'evolution'
      ? (
          Boolean(instance) &&
          evolutionState === 'open'
        )
      : true;

  const limit =
    Number(
      aiSettings.value
        .monthly_cost_limit_brl || 0
    );

  const cost =
    Number(
      companyUsage.value.month?.cost_brl ||
      0
    );

  const limitPercent =
    limit > 0
      ? (cost / limit) * 100
      : 0;

  const aiEnabled =
    aiSettings.value.enabled === true;

  let status = 'healthy';
  let label = 'Saudável';
  let reason = 'Operação normal';

  if (
    provider === 'evolution' &&
    whatsappConfigured &&
    !whatsappOnline
  ) {
    status = 'critical';
    label = 'Crítico';
    reason = 'WhatsApp desconectado';
  } else if (
    limit > 0 &&
    limitPercent >= 100
  ) {
    status = 'critical';
    label = 'Crítico';
    reason = 'Limite da IA atingido';
  } else if (
    limit > 0 &&
    limitPercent >= 80
  ) {
    status = 'warning';
    label = 'Atenção';
    reason = 'Consumo elevado da IA';
  } else if (!aiEnabled) {
    status = 'inactive';
    label = 'Inativo';
    reason = 'IA desativada';
  }

  let whatsappLabel = 'Não configurado';

  if (provider === 'official') {
    whatsappLabel = 'API Oficial';
  } else if (!instance) {
    whatsappLabel = 'Não configurado';
  } else if (whatsappOnline) {
    whatsappLabel = 'Online';
  } else {
    whatsappLabel = 'Offline';
  }

  let aiLabel = 'Desativada';

  if (aiEnabled) {
    if (
      isMonthlyLimitReached.value &&
      aiSettings.value
        .monthly_limit_action === 'pause'
    ) {
      aiLabel = 'Pausada por limite';
    } else {
      aiLabel = 'Ativa';
    }
  }

  return {
    status,
    label,
    reason,
    whatsappLabel,
    whatsappOnline,
    whatsappConfigured,
    aiLabel,
    aiEnabled,
    limit,
    cost,
    limitPercent,
  };
});


const operationalHealthBadgeClass =
  computed(() => {
    switch (
      operationalHealth.value.status
    ) {
      case 'critical':
        return (
          'bg-n-ruby-3 text-n-ruby-11 ' +
          'border-n-ruby-6'
        );

      case 'warning':
        return (
          'bg-n-amber-3 text-n-amber-11 ' +
          'border-n-amber-6'
        );

      case 'inactive':
        return (
          'bg-n-slate-3 text-n-slate-11 ' +
          'border-n-slate-6'
        );

      default:
        return (
          'bg-n-teal-3 text-n-teal-11 ' +
          'border-n-teal-6'
        );
    }
  });


const operationalHealthDotClass =
  computed(() => {
    switch (
      operationalHealth.value.status
    ) {
      case 'critical':
        return 'bg-n-ruby-9';

      case 'warning':
        return 'bg-n-amber-9';

      case 'inactive':
        return 'bg-n-slate-8';

      default:
        return 'bg-n-teal-9';
    }
  });


const operationalLimitClass =
  computed(() => {
    const percent =
      operationalHealth.value
        .limitPercent;

    if (percent >= 100) {
      return 'text-n-ruby-11';
    }

    if (percent >= 80) {
      return 'text-n-amber-11';
    }

    return 'text-n-slate-12';
  });

const formatOperationalEventDate = value => {
  if (!value) return '-';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleString('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
};

const operationalEventClass = severity => {
  switch (severity) {
    case 'critical':
      return 'border-n-ruby-6 bg-n-ruby-3 text-n-ruby-11';

    case 'warning':
      return 'border-n-amber-6 bg-n-amber-3 text-n-amber-11';

    case 'success':
      return 'border-n-teal-6 bg-n-teal-3 text-n-teal-11';

    default:
      return 'border-n-blue-6 bg-n-blue-3 text-n-blue-11';
  }
};

const operationalEventSeverityLabel = severity => {
  switch (severity) {
    case 'critical':
      return 'Crítico';

    case 'warning':
      return 'Atenção';

    case 'success':
      return 'Normal';

    default:
      return 'Informação';
  }
};

const automationOverview = computed(() => {
  const localResponses =
    Number(
      localUsage.value.month?.responses || 0
    );

  const aiRequests =
    Number(
      localUsage.value.ai_month_requests || 0
    );

  const totalAutomated =
    localResponses + aiRequests;

  const localPercentage =
    totalAutomated > 0
      ? (
          localResponses /
          totalAutomated
        ) * 100
      : 0;

  const aiCost =
    Number(
      companyUsage.value.month?.cost_brl ||
      0
    );

  const averageAiCost =
    aiRequests > 0
      ? aiCost / aiRequests
      : 0;

  const estimatedSavings =
    averageAiCost * localResponses;

  const monthlyLimit =
    Number(
      aiSettings.value
        .monthly_cost_limit_brl || 0
    );

  const limitPercentage =
    monthlyLimit > 0
      ? (aiCost / monthlyLimit) * 100
      : 0;

  let status = 'healthy';
  let statusLabel = 'Saudável';

  if (
    aiSettings.value.enabled === false
  ) {
    status = 'disabled';
    statusLabel = 'IA desativada';
  } else if (
    monthlyLimit > 0 &&
    aiCost >= monthlyLimit
  ) {
    status = 'critical';
    statusLabel = 'Limite atingido';
  } else if (
    monthlyLimit > 0 &&
    limitPercentage >= 80
  ) {
    status = 'warning';
    statusLabel = 'Atenção';
  }

  return {
    localResponses,
    aiRequests,
    totalAutomated,
    localPercentage,
    aiCost,
    averageAiCost,
    estimatedSavings,
    monthlyLimit,
    limitPercentage,
    status,
    statusLabel,
  };
});

const localRuleEntries = computed(() =>
  Object.entries(
    aiSettings.value.local_reply_rules || {}
  )
);

const localRuleTitle = (key, rule) =>
  String(
    rule?.name ||
    localRuleLabels[key] ||
    key
  );

const localRuleMonthCount = key =>
  Number(
    localUsage.value.month?.by_type?.[key] || 0
  );

const localRuleRanking = computed(() => {
  const byType =
    localUsage.value.month?.by_type || {};

  const rules =
    aiSettings.value.local_reply_rules || {};

  const total =
    Number(localUsage.value.month?.responses || 0);

  return Object.entries(byType)
    .map(([key, count]) => {
      const value = Number(count || 0);

      return {
        key,
        name: localRuleTitle(
          key,
          rules[key]
        ),
        count: value,
        percentage:
          total > 0
            ? (value / total) * 100
            : 0,
      };
    })
    .filter(item => item.count > 0)
    .sort((a, b) => b.count - a.count);
});

const formatLocalRankingPercentage = value =>
  Number(value || 0).toLocaleString(
    'pt-BR',
    {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }
  );





const createLocalRuleKey = () => {
  let key;

  do {
    key =
      `custom_${Date.now()}_` +
      Math.random()
        .toString(36)
        .slice(2, 7);
  } while (
    aiSettings.value.local_reply_rules?.[key]
  );

  return key;
};

const addLocalRule = () => {
  if (!aiSettings.value.local_reply_rules) {
    aiSettings.value.local_reply_rules = {};
  }

  const key = createLocalRuleKey();

  aiSettings.value.local_reply_rules[key] = {
    name: 'Nova categoria',
    enabled: true,
    phrases: [],
    match_mode: 'exact',
    reply: '',
      action: 'reply_only',
      sector: 'geral',
      team_id: null,
  };
};

const localIntentLibrary = [
  {
    key: 'human_agent',
    name: 'Atendente humano',
    description:
      'Encaminha pedidos para falar com uma pessoa.',
    phrases: [
      'atendente',
      'atendimento humano',
      'falar com atendente',
      'quero um atendente',
      'quero falar com alguém',
      'falar com alguém',
      'falar com uma pessoa',
      'quero falar com uma pessoa',
    ],
    action: 'transfer_only',
    sector: 'geral',
  },
  {
    key: 'commercial',
    name: 'Comercial',
    description:
      'Identifica clientes interessados em contratar.',
    phrases: [
      'quero contratar',
      'contratar internet',
      'quero internet',
      'planos de internet',
      'quais os planos',
      'qual o valor dos planos',
      'valor da internet',
      'tem plano',
      'assinar internet',
    ],
    action: 'transfer_only',
    sector: 'comercial',
  },
  {
    key: 'financial',
    name: 'Financeiro / Boleto',
    description:
      'Direciona assuntos de boleto, fatura e pagamento.',
    phrases: [
      'boleto',
      'segunda via',
      'segunda via do boleto',
      'fatura',
      'minha fatura',
      'vencimento',
      'pagamento',
      'pagar fatura',
      'pagar boleto',
      'conta vencida',
      'boleto vencido',
    ],
    action: 'transfer_only',
    sector: 'financeiro',
  },
  {
    key: 'support',
    name: 'Suporte técnico',
    description:
      'Direciona problemas de conexão para o suporte.',
    phrases: [
      'sem internet',
      'internet caiu',
      'internet não funciona',
      'internet nao funciona',
      'sem conexão',
      'sem conexao',
      'internet lenta',
      'lentidão',
      'lentidao',
      'problema na internet',
      'problema com internet',
      'suporte técnico',
      'suporte tecnico',
    ],
    action: 'transfer_only',
    sector: 'suporte',
  },
  {
    key: 'cancellation',
    name: 'Cancelamento',
    description:
      'Direciona pedidos explícitos de cancelamento.',
    phrases: [
      'quero cancelar',
      'quero cancelar internet',
      'cancelar internet',
      'cancelar meu plano',
      'cancelar contrato',
      'pedido de cancelamento',
      'solicitar cancelamento',
    ],
    action: 'transfer_only',
    sector: 'cancelamento',
  },
];

const hasLocalIntent = intentKey => {
  const rules =
    aiSettings.value.local_reply_rules || {};

  return Object.values(rules).some(
    rule =>
      String(rule?.library_intent || '') ===
      String(intentKey || '')
  );
};

const addLocalIntent = intentKey => {
  const preset =
    localIntentLibrary.find(
      item => item.key === intentKey
    );

  if (!preset) {
    aiError.value =
      'Intenção selecionada não foi encontrada.';
    return;
  }

  if (hasLocalIntent(preset.key)) {
    aiError.value =
      `A intenção "${preset.name}" já foi adicionada.`;
    aiSuccess.value = '';
    return;
  }

  if (!aiSettings.value.local_reply_rules) {
    aiSettings.value.local_reply_rules = {};
  }

  const key = createLocalRuleKey();

  aiSettings.value.local_reply_rules[key] = {
    name: preset.name,
    enabled: true,
    phrases: [...preset.phrases],
    match_mode: 'contains_any',
    reply: '',
    action: preset.action,
    sector: preset.sector,
    team_id: null,
    library_intent: preset.key,
  };

  aiError.value = '';

  aiSuccess.value =
    `Intenção "${preset.name}" adicionada. ` +
    'Revise a categoria e salve as configurações.';
};

const removeLocalRule = key => {
  if (
    !aiSettings.value.local_reply_rules ||
    !aiSettings.value.local_reply_rules[key]
  ) {
    return;
  }

  const title =
    localRuleTitle(
      key,
      aiSettings.value.local_reply_rules[key]
    );

  if (
    !window.confirm(
      `Excluir a categoria "${title}"?`
    )
  ) {
    return;
  }

  delete aiSettings.value.local_reply_rules[key];
};

const sanitizeLocalRuleForImport = rule => {
  if (
    !rule ||
    typeof rule !== 'object' ||
    Array.isArray(rule)
  ) {
    return null;
  }

  const phrases =
    Array.isArray(rule.phrases)
      ? rule.phrases
          .map(item => String(item || '').trim())
          .filter(Boolean)
      : [];

  const allowedLocalActions = [
      'reply_only',
      'reply_and_transfer',
      'transfer_only',
    ];

    const allowedLocalSectors = [
      'geral',
      'comercial',
      'financeiro',
      'suporte',
      'cancelamento',
    ];

    const requestedMatchMode =
      String(rule.match_mode || 'exact')
        .trim()
        .toLowerCase();

    const matchMode =
      requestedMatchMode === 'contains_any'
        ? 'contains_any'
        : 'exact';

    const requestedAction =
      String(rule.action || 'reply_only')
        .trim()
        .toLowerCase();

    const requestedSector =
      String(rule.sector || 'geral')
        .trim()
        .toLowerCase();

    return {
    team_id:
      Number(rule.team_id || 0) > 0
        ? Number(rule.team_id)
        : null,
    name: String(rule.name || '').trim(),
    enabled: rule.enabled !== false,
    phrases,
    match_mode: matchMode,
    library_intent:
      String(rule.library_intent || '').trim(),
    reply: String(rule.reply || '').trim(),
      action: allowedLocalActions.includes(requestedAction)
        ? requestedAction
        : 'reply_only',
      sector: allowedLocalSectors.includes(requestedSector)
        ? requestedSector
        : 'geral',
  };
};

const sanitizeLocalRulesImport = rules => {
  if (
    !rules ||
    typeof rules !== 'object' ||
    Array.isArray(rules)
  ) {
    throw new Error(
      'O arquivo não possui categorias válidas.'
    );
  }

  const sanitized = {};

  Object.entries(rules).forEach(([key, rule]) => {
    const cleanRule =
      sanitizeLocalRuleForImport(rule);

    if (!cleanRule) {
      return;
    }

    let cleanKey =
      String(key || '')
        .trim()
        .replace(/[^a-zA-Z0-9_-]/g, '_')
        .slice(0, 100);

    if (!cleanKey) {
      cleanKey = createLocalRuleKey();
    }

    let finalKey = cleanKey;
    let counter = 2;

    while (sanitized[finalKey]) {
      finalKey = `${cleanKey}_${counter}`;
      counter += 1;
    }

    sanitized[finalKey] = cleanRule;
  });

  if (!Object.keys(sanitized).length) {
    throw new Error(
      'Nenhuma categoria válida foi encontrada.'
    );
  }

  return sanitized;
};

const localRulesExportFileName = () => {
  const companyName =
    String(company.value?.name || 'empresa')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') ||
    'empresa';

  return `respostas-locais-${companyName}.json`;
};

const exportLocalRules = () => {
  aiError.value = '';
  aiSuccess.value = '';

  const rules =
    aiSettings.value.local_reply_rules || {};

  if (!Object.keys(rules).length) {
    aiError.value =
      'Não há categorias para exportar.';
    return;
  }

  const payload = {
    format: 'chatwoot-local-replies',
    version: 1,
    exported_at: new Date().toISOString(),

    company: {
      name: String(company.value?.name || ''),
    },

    local_replies_enabled:
      aiSettings.value.local_replies_enabled !== false,

    local_reply_rules: rules,
  };

  const blob = new Blob(
    [JSON.stringify(payload, null, 2)],
    {
      type: 'application/json;charset=utf-8',
    }
  );

  const url =
    URL.createObjectURL(blob);

  const link =
    document.createElement('a');

  link.href = url;
  link.download =
    localRulesExportFileName();

  document.body.appendChild(link);

  link.click();
  link.remove();

  URL.revokeObjectURL(url);

  aiSuccess.value =
    'Regras locais exportadas com sucesso.';
};

const openLocalRulesImport = () => {
  aiError.value = '';
  aiSuccess.value = '';

  if (!localRulesImportInput.value) {
    return;
  }

  localRulesImportInput.value.value = '';
  localRulesImportInput.value.click();
};

const importLocalRules = async event => {
  aiError.value = '';
  aiSuccess.value = '';

  const file =
    event.target?.files?.[0];

  if (!file) {
    return;
  }

  try {
    if (file.size > 2 * 1024 * 1024) {
      throw new Error(
        'O arquivo é muito grande. O limite é 2 MB.'
      );
    }

    const text =
      await file.text();

    let data;

    try {
      data = JSON.parse(text);
    } catch {
      throw new Error(
        'O arquivo selecionado não é um JSON válido.'
      );
    }

    let importedRules;

    if (
      data?.format === 'chatwoot-local-replies' &&
      Number(data?.version) === 1
    ) {
      importedRules =
        data.local_reply_rules;
    } else if (
      data?.local_reply_rules &&
      typeof data.local_reply_rules === 'object'
    ) {
      importedRules =
        data.local_reply_rules;
    } else {
      throw new Error(
        'Este arquivo não possui um formato ' +
        'de respostas locais reconhecido.'
      );
    }

    const sanitized =
      sanitizeLocalRulesImport(
        importedRules
      );

    const count =
      Object.keys(sanitized).length;

    const confirmed =
      window.confirm(
        `Importar ${count} categoria(s)?\n\n` +
        'As categorias atualmente exibidas serão ' +
        'substituídas.\n\n' +
        'Nada será salvo no servidor até você clicar ' +
        'em "Salvar configurações da IA".'
      );

    if (!confirmed) {
      return;
    }

    aiSettings.value.local_reply_rules =
      sanitized;

    if (
      typeof data.local_replies_enabled ===
      'boolean'
    ) {
      aiSettings.value.local_replies_enabled =
        data.local_replies_enabled;
    }

    aiSuccess.value =
      `${count} categoria(s) importada(s). ` +
      'Revise e clique em ' +
      '"Salvar configurações da IA".';

  } catch (error) {
    aiError.value =
      error?.message ||
      'Não foi possível importar o arquivo.';
  } finally {
    if (event.target) {
      event.target.value = '';
    }
  }
};

const localPhrasesText = type => {
  const phrases =
    aiSettings.value.local_reply_rules?.[type]?.phrases;

  return Array.isArray(phrases)
    ? phrases.join('\n')
    : '';
};

const setLocalPhrasesText = (type, value) => {
  if (!aiSettings.value.local_reply_rules) {
    aiSettings.value.local_reply_rules = {};
  }

  if (!aiSettings.value.local_reply_rules[type]) {
    aiSettings.value.local_reply_rules[type] = {
      enabled: true,
      phrases: [],
      reply: '',
        action: 'reply_only',
        sector: 'geral',
    };
  }

  aiSettings.value.local_reply_rules[type].phrases =
    String(value || '')
      .split('\n')
      .map(item => item.trim())
      .filter(Boolean);
};

const saveAiManagement = async () => {
  if (localPhraseConflicts.value.length) {
    aiSuccess.value = '';
    aiError.value =
      'Corrija as frases duplicadas antes de salvar. ' +
      'A mesma frase não pode existir em categorias diferentes.';
    return;
  }

  const localTransferError =
    validateLocalTransferRules();

  if (localTransferError) {
    aiSuccess.value = '';
    aiError.value = localTransferError;
    return;
  }

  aiSaving.value = true;
  aiError.value = '';
  aiSuccess.value = '';

  try {
    const payload = {
      enabled: aiSettings.value.enabled,
      model: aiSettings.value.model,
      assistant_name:
        aiSettings.value.assistant_name,
      commercial_team_id:
        aiSettings.value.commercial_team_id ||
        null,
      financial_team_id:
        aiSettings.value.financial_team_id ||
        null,
      support_team_id:
        aiSettings.value.support_team_id ||
        null,
      cancellation_team_id:
        aiSettings.value.cancellation_team_id ||
        null,
      general_team_id:
        aiSettings.value.general_team_id ||
        null,

      allowed_inbox_ids:
        Array.isArray(aiSettings.value.allowed_inbox_ids)
          ? aiSettings.value.allowed_inbox_ids
              .map(Number)
              .filter(Boolean)
          : [],

      auto_transfer:
        aiSettings.value.auto_transfer,

      add_waiting_label:
        aiSettings.value.add_waiting_label,

      stop_ai_after_transfer:
        aiSettings.value.stop_ai_after_transfer,

      transfer_message:
        aiSettings.value.transfer_message || '',

      system_prompt:
        aiSettings.value.system_prompt || '',

      usage_profile:
        aiSettings.value.usage_profile || 'balanced',

      history_limit:
        Math.max(
          1,
          Math.min(
            Number(aiSettings.value.history_limit) || 15,
            50
          )
        ),

      max_output_tokens:
        Math.max(
          100,
          Math.min(
            Number(aiSettings.value.max_output_tokens) || 700,
            2000
          )
        ),

      monthly_cost_limit_brl:
        Math.max(
          0,
          Number(
            aiSettings.value.monthly_cost_limit_brl
          ) || 0
        ),

      monthly_limit_action:
        aiSettings.value.monthly_limit_action ||
        'alert',
    
        local_replies_enabled:
          aiSettings.value.local_replies_enabled !== false,

        local_reply_rules:
          aiSettings.value.local_reply_rules || {},
      company_variables:
        aiSettings.value.company_variables || {},

      commercial_flows:
        aiSettings.value.commercial_flows || {},

};

    const response =
      await AiBotSettingsAPI
        .updateSettingsForAccount(
          companyId.value,
          payload
        );

    aiSettings.value = {
      ...aiSettings.value,
      ...(response.data || {}),
    };

    aiSuccess.value =
      'Configurações da IA atualizadas com sucesso.';

    await loadCompany();
  } catch (err) {
    console.error(
      'Erro ao salvar gerenciamento da IA:',
      err
    );

    aiError.value =
      err.response?.data?.error ||
      'Não foi possível salvar as configurações da IA.';
  } finally {
    aiSaving.value = false;
  }
};

const loadOperationalHistory = async () => {
  operationalHistoryLoading.value = true;
  operationalHistoryError.value = '';

  try {
    const response =
      await AiBotSettingsAPI.getOperationalHistoryForAccount(
        companyId.value,
        100
      );

    operationalHistory.value =
      response.data?.events || [];
  } catch (error) {
    operationalHistory.value = [];

    operationalHistoryError.value =
      'Não foi possível carregar o histórico operacional.';

    console.error(
      'Erro ao carregar histórico operacional:',
      error
    );
  } finally {
    operationalHistoryLoading.value = false;
  }
};

const loadCompany = async () => {
  loading.value = true;
  error.value = '';

  try {
    const response =
      await AiBotSettingsAPI.getCompany(
        companyId.value
      );

    company.value = response.data;

    form.value = {
      name: response.data?.name || '',
      responsible:
        response.data?.responsible || '',
      email:
        response.data?.email || '',
      phone:
        response.data?.phone || '',
    };
  } catch (err) {
    console.error(
      'Erro ao carregar empresa:',
      err
    );

    error.value =
      err.response?.data?.error ||
      'Não foi possível carregar a empresa.';
  } finally {
    loading.value = false;
  }
};

const saveCompany = async () => {
  if (!form.value.name.trim()) {
    error.value =
      'Informe o nome da empresa.';
    return;
  }

  saving.value = true;
  error.value = '';
  success.value = '';

  try {
    await AiBotSettingsAPI.updateCompany(
      companyId.value,
      {
        name: form.value.name.trim(),
        responsible:
          form.value.responsible.trim(),
        email:
          form.value.email.trim(),
        phone:
          form.value.phone.trim(),
      }
    );

    success.value =
      'Dados da empresa atualizados.';

    await loadCompany();
  } catch (err) {
    console.error(
      'Erro ao salvar empresa:',
      err
    );

    error.value =
      err.response?.data?.error ||
      'Não foi possível atualizar a empresa.';
  } finally {
    saving.value = false;
  }
};

const openAiSettings = () => {
  router.push({
    name: 'ai_bot_settings',
    params: {
      accountId: companyId.value,
    },
  });
};

const backToCompanies = () => {
  router.push({
    name: 'ai_companies_dashboard_index',
  });
};

const getEvolutionState = data => {
  return (
    data?.response?.instance?.state ||
    data?.response?.state ||
    data?.state ||
    ''
  );
};

const getEvolutionPhone = data => {
  const raw =
    data?.response?.instance?.ownerJid ||
    data?.response?.ownerJid ||
    data?.response?.instance?.number ||
    data?.response?.number ||
    '';

  return String(raw || '')
    .replace('@s.whatsapp.net', '')
    .replace(/\D/g, '');
};

const stopEvolutionPolling = () => {
  if (evolutionPollTimer) {
    clearInterval(evolutionPollTimer);
    evolutionPollTimer = null;
  }
};

const loadEvolutionStatus = async () => {
  if (
    company.value?.ai?.whatsapp_provider !==
    'evolution'
  ) {
    return;
  }

  evolutionLoading.value = true;
  evolutionError.value = '';

  try {
    const response =
      await AiBotSettingsAPI
        .getEvolutionStatusForAccount(
          companyId.value
        );

    evolutionStatus.value = response.data;
    evolutionPhone.value =
      getEvolutionPhone(response.data);

    if (
      getEvolutionState(response.data) ===
      'open'
    ) {
      evolutionQr.value = '';
      stopEvolutionPolling();
    }
  } catch (err) {
    console.error(
      'Erro ao consultar Evolution:',
      err
    );

    evolutionError.value =
      err.response?.data?.error ||
      'Não foi possível consultar o status do WhatsApp.';
  } finally {
    evolutionLoading.value = false;
  }
};

const startEvolutionPolling = () => {
  stopEvolutionPolling();

  evolutionPollTimer = setInterval(
    async () => {
      await loadEvolutionStatus();

      if (
        getEvolutionState(
          evolutionStatus.value
        ) === 'open'
      ) {
        stopEvolutionPolling();
      }
    },
    3000
  );
};

const generateEvolutionQr = async () => {
  evolutionLoading.value = true;
  evolutionError.value = '';
  evolutionQr.value = '';

  try {
    const response =
      await AiBotSettingsAPI
        .getEvolutionQrForAccount(
          companyId.value
        );

    const data =
      response.data?.response ||
      response.data ||
      {};

    evolutionQr.value =
      data.base64 ||
      data.qrcode?.base64 ||
      data.qr ||
      data.code ||
      '';

    await loadEvolutionStatus();

    if (
      getEvolutionState(
        evolutionStatus.value
      ) !== 'open'
    ) {
      startEvolutionPolling();
    }
  } catch (err) {
    console.error(
      'Erro ao gerar QR Code:',
      err
    );

    evolutionError.value =
      err.response?.data?.error ||
      'Não foi possível gerar o QR Code.';
  } finally {
    evolutionLoading.value = false;
  }
};

const whatsappProviderLabel =
  computed(() => {
    if (
      company.value?.ai
        ?.whatsapp_provider === 'official'
    ) {
      return 'API Oficial do WhatsApp';
    }

    return 'Evolution API';
  });

const aiLabel = computed(() =>
  company.value?.ai?.enabled
    ? 'Ativada'
    : 'Desativada'
);

onMounted(async () => {
  loadOperationalHistory();
  await Promise.all([
    loadCompany(),
    loadAiManagement(),
    loadCompanyUsage(),
  ]);

  if (
    company.value?.ai?.whatsapp_provider ===
    'evolution'
  ) {
    await loadEvolutionStatus();
  }
});

onBeforeUnmount(() => {
  stopEvolutionPolling();
});
</script>

<template>
  <div
    class="w-full h-full overflow-auto bg-n-background"
  >
    <div class="max-w-5xl px-8 py-8 mx-auto">
      <div
        class="flex items-start justify-between gap-4 mb-8"
      >
        <div>
          <button
            type="button"
            class="mb-4 text-sm text-n-slate-11 hover:text-n-slate-12"
            @click="backToCompanies"
          >
            ← Voltar para Empresas IA
          </button>

          <h1
            class="text-2xl font-semibold text-n-slate-12"
          >
            {{
              company?.name ||
              'Gerenciar empresa'
            }}
          </h1>

          <p
            class="mt-1 text-sm text-n-slate-11"
          >
            Account {{ companyId }}
          </p>
        </div>

        <button
          type="button"
          class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand"
          @click="openAiSettings"
        >
          Configurações da IA
        </button>
      </div>

      <div
        v-if="error"
        class="p-3 mb-5 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11"
      >
        {{ error }}
      </div>

      <div
        v-if="success"
        class="p-3 mb-5 text-sm rounded-lg bg-n-teal-3 text-n-teal-11"
      >
        {{ success }}
      </div>

      <div
        v-if="loading"
        class="py-10 text-center text-n-slate-11"
      >
        Carregando empresa...
      </div>

      <template v-else-if="company">
        
          <!-- DIAGNÓSTICO OPERACIONAL -->
          <div
            class="p-6 mb-5 border rounded-xl border-n-weak bg-n-background"
          >
            <div
              class="flex flex-wrap items-start justify-between gap-4"
            >
              <div>
                <h2
                  class="text-lg font-semibold text-n-slate-12"
                >
                  Diagnóstico operacional
                </h2>

                <p
                  class="mt-1 text-sm text-n-slate-10"
                >
                  Situação atual dos principais serviços desta empresa.
                </p>
              </div>

              <span
                class="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium border rounded-full"
                :class="operationalHealthBadgeClass"
              >
                <span
                  class="w-2 h-2 rounded-full"
                  :class="operationalHealthDotClass"
                />

                {{ operationalHealth.label }}
              </span>
            </div>


            <div
              class="p-4 mt-5 border rounded-xl"
              :class="{
                'border-n-teal-6 bg-n-teal-2':
                  operationalHealth.status === 'healthy',

                'border-n-amber-6 bg-n-amber-2':
                  operationalHealth.status === 'warning',

                'border-n-ruby-6 bg-n-ruby-2':
                  operationalHealth.status === 'critical',

                'border-n-weak bg-n-alpha-2':
                  operationalHealth.status === 'inactive',
              }"
            >
              <div
                class="text-xs font-medium text-n-slate-10"
              >
                Status geral
              </div>

              <div
                class="mt-1 text-base font-semibold"
                :class="{
                  'text-n-teal-11':
                    operationalHealth.status === 'healthy',

                  'text-n-amber-11':
                    operationalHealth.status === 'warning',

                  'text-n-ruby-11':
                    operationalHealth.status === 'critical',

                  'text-n-slate-11':
                    operationalHealth.status === 'inactive',
                }"
              >
                {{ operationalHealth.reason }}
              </div>
            </div>


            <div
              class="grid grid-cols-1 gap-3 mt-4 sm:grid-cols-2 lg:grid-cols-4"
            >
              <!-- WHATSAPP -->
              <div
                class="p-4 border rounded-xl border-n-weak bg-n-alpha-1"
              >
                <p
                  class="text-xs font-medium text-n-slate-10"
                >
                  WhatsApp
                </p>

                <p
                  class="mt-2 text-lg font-semibold"
                  :class="
                    operationalHealth.whatsappLabel === 'Offline'
                      ? 'text-n-ruby-11'
                      : operationalHealth.whatsappLabel === 'Online'
                        ? 'text-n-teal-11'
                        : 'text-n-slate-12'
                  "
                >
                  {{ operationalHealth.whatsappLabel }}
                </p>

                <p
                  class="mt-1 text-xs text-n-slate-10"
                >
                  {{ whatsappProviderLabel }}
                </p>
              </div>


              <!-- IA -->
              <div
                class="p-4 border rounded-xl border-n-weak bg-n-alpha-1"
              >
                <p
                  class="text-xs font-medium text-n-slate-10"
                >
                  Inteligência Artificial
                </p>

                <p
                  class="mt-2 text-lg font-semibold"
                  :class="
                    operationalHealth.aiEnabled
                      ? 'text-n-teal-11'
                      : 'text-n-slate-11'
                  "
                >
                  {{ operationalHealth.aiLabel }}
                </p>

                <p
                  class="mt-1 text-xs text-n-slate-10"
                >
                  {{
                    aiSettings.model ||
                    'gpt-5.6-luna'
                  }}
                </p>
              </div>


              <!-- LIMITE -->
              <div
                class="p-4 border rounded-xl border-n-weak bg-n-alpha-1"
              >
                <p
                  class="text-xs font-medium text-n-slate-10"
                >
                  Limite mensal IA
                </p>

                <p
                  v-if="operationalHealth.limit > 0"
                  class="mt-2 text-lg font-semibold"
                  :class="operationalLimitClass"
                >
                  {{
                    Math.min(
                      operationalHealth.limitPercent,
                      999
                    ).toFixed(1)
                  }}%
                </p>

                <p
                  v-else
                  class="mt-2 text-lg font-semibold text-n-slate-12"
                >
                  Sem limite
                </p>

                <p
                  v-if="operationalHealth.limit > 0"
                  class="mt-1 text-xs text-n-slate-10"
                >
                  {{
                    formatBRL(
                      operationalHealth.limit
                    )
                  }}
                </p>
              </div>


              <!-- CUSTO -->
              <div
                class="p-4 border rounded-xl border-n-weak bg-n-alpha-1"
              >
                <p
                  class="text-xs font-medium text-n-slate-10"
                >
                  Custo IA no mês
                </p>

                <p
                  class="mt-2 text-lg font-semibold text-n-slate-12"
                >
                  {{
                    formatBRL(
                      operationalHealth.cost
                    )
                  }}
                </p>

                <p
                  class="mt-1 text-xs text-n-slate-10"
                >
                  {{
                    Number(
                      companyUsage.month?.requests ||
                      0
                    ).toLocaleString('pt-BR')
                  }}
                  chamadas
                </p>
              </div>
            </div>


            <div
              v-if="operationalHealth.limit > 0"
              class="mt-5"
            >
              <div
                class="flex items-center justify-between gap-4 mb-2 text-xs"
              >
                <span
                  class="text-n-slate-10"
                >
                  Utilização do limite mensal
                </span>

                <span
                  class="font-medium"
                  :class="operationalLimitClass"
                >
                  {{
                    operationalHealth
                      .limitPercent
                      .toFixed(1)
                  }}%
                </span>
              </div>

              <div
                class="w-full h-2 overflow-hidden rounded-full bg-n-alpha-3"
              >
                <div
                  class="h-full transition-all rounded-full"
                  :class="{
                    'bg-n-teal-9':
                      operationalHealth.limitPercent < 80,

                    'bg-n-amber-9':
                      operationalHealth.limitPercent >= 80 &&
                      operationalHealth.limitPercent < 100,

                    'bg-n-ruby-9':
                      operationalHealth.limitPercent >= 100,
                  }"
                  :style="{
                    width:
                      `${Math.min(
                        operationalHealth.limitPercent,
                        100
                      )}%`,
                  }"
                />
              </div>
            </div>
          </div>


<!-- DADOS -->
        <div
          class="p-6 mb-5 border rounded-xl border-n-weak bg-n-background"
        >
          <h2
            class="mb-1 text-lg font-semibold text-n-slate-12"
          >
            Dados da empresa
          </h2>

          <p
            class="mb-6 text-sm text-n-slate-11"
          >
            Informações principais deste cliente.
          </p>

          <div
            class="grid grid-cols-1 gap-4 md:grid-cols-2"
          >
            <div>
              <label
                class="block mb-1 text-sm font-medium text-n-slate-12"
              >
                Nome da empresa *
              </label>

              <input
                v-model="form.name"
                type="text"
                class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
              />
            </div>

            <div>
              <label
                class="block mb-1 text-sm font-medium text-n-slate-12"
              >
                Responsável
              </label>

              <input
                v-model="form.responsible"
                type="text"
                placeholder="Nome do responsável"
                class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
              />
            </div>

            <div>
              <label
                class="block mb-1 text-sm font-medium text-n-slate-12"
              >
                E-mail
              </label>

              <input
                v-model="form.email"
                type="email"
                class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
              />
            </div>

            <div>
              <label
                class="block mb-1 text-sm font-medium text-n-slate-12"
              >
                Telefone
              </label>

              <input
                v-model="form.phone"
                type="text"
                placeholder="(91) 99999-9999"
                class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
              />
            </div>
          </div>

          <div
            class="flex justify-end mt-6"
          >
            <button
              type="button"
              class="px-5 py-2 text-sm font-medium text-white rounded-lg bg-n-brand disabled:opacity-50"
              :disabled="saving"
              @click="saveCompany"
            >
              {{
                saving
                  ? 'Salvando...'
                  : 'Salvar alterações'
              }}
            </button>
          </div>
        </div>

        <!-- WHATSAPP -->
        <div
          class="p-6 mb-5 border rounded-xl border-n-weak bg-n-background"
        >
          <div
            class="flex items-start justify-between gap-4"
          >
            <div>
              <h2
                class="text-lg font-semibold text-n-slate-12"
              >
                Canais de atendimento
              </h2>

              <p
                class="mt-1 text-sm text-n-slate-11"
              >
                Gerencie os canais de atendimento desta empresa.
              </p>
            </div>

            <button
              v-if="
                company.ai?.whatsapp_provider ===
                'evolution'
              "
              type="button"
              class="px-3 py-2 text-sm border rounded-lg border-n-weak text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-50"
              :disabled="evolutionLoading"
              @click="loadEvolutionStatus"
            >
              {{
                evolutionLoading
                  ? 'Consultando...'
                  : 'Atualizar status'
              }}
            </button>
          </div>

          <div class="mt-6">
            <div class="flex items-center gap-2">
              <div class="font-semibold text-n-slate-12">
                WhatsApp
              </div>
            </div>
          </div>

          <div
            class="grid grid-cols-1 gap-5 mt-4 md:grid-cols-2"
          >
            <div>
              <div
                class="text-sm text-n-slate-10"
              >
                Provedor
              </div>

              <div
                class="mt-1 font-medium text-n-slate-12"
              >
                {{ whatsappProviderLabel }}
              </div>
            </div>

            <div
              v-if="
                company.ai?.whatsapp_provider ===
                'evolution'
              "
            >
              <div
                class="text-sm text-n-slate-10"
              >
                Instância Evolution
              </div>

              <div
                class="mt-1 font-medium text-n-slate-12"
              >
                {{
                  company.ai
                    ?.evolution_instance_name ||
                  'Não configurada'
                }}
              </div>
            </div>
          </div>

          <!-- EVOLUTION -->
          <template
            v-if="
              company.ai?.whatsapp_provider ===
              'evolution'
            "
          >
            <div
              v-if="evolutionError"
              class="p-3 mt-5 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11"
            >
              {{ evolutionError }}
            </div>

            <div
              class="p-4 mt-5 border rounded-lg border-n-weak bg-n-alpha-1"
            >
              <div
                class="flex items-center gap-3"
              >
                <span
                  class="inline-block w-3 h-3 rounded-full"
                  :class="
                    getEvolutionState(
                      evolutionStatus
                    ) === 'open'
                      ? 'bg-n-teal-9'
                      : 'bg-n-ruby-9'
                  "
                />

                <div>
                  <div
                    class="font-medium text-n-slate-12"
                  >
                    {{
                      getEvolutionState(
                        evolutionStatus
                      ) === 'open'
                        ? 'Conectado'
                        : 'Desconectado'
                    }}
                  </div>

                  <div
                    v-if="evolutionPhone"
                    class="mt-1 text-sm text-n-slate-11"
                  >
                    Número:
                    {{ evolutionPhone }}
                  </div>

                  <div
                    v-else
                    class="mt-1 text-sm text-n-slate-11"
                  >
                    Número: Não conectado
                  </div>
                </div>
              </div>
            </div>

            <div
              class="flex flex-wrap gap-3 mt-5"
            >
              <button
                v-if="
                  getEvolutionState(
                    evolutionStatus
                  ) !== 'open'
                "
                type="button"
                class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand disabled:opacity-50"
                :disabled="evolutionLoading"
                @click="generateEvolutionQr"
              >
                {{
                  evolutionLoading
                    ? 'Aguarde...'
                    : 'Gerar QR Code'
                }}
              </button>

              <div
                v-else
                class="flex items-center text-sm font-medium text-n-teal-11"
              >
                WhatsApp conectado
              </div>

              <button
                type="button"
                class="px-4 py-2 text-sm font-medium border rounded-lg border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
                @click="openAiSettings"
              >
                Configurações avançadas
              </button>
            </div>

            <div
              v-if="evolutionQr"
              class="mt-6"
            >
              <div
                class="mb-3 text-sm font-medium text-n-slate-12"
              >
                Leia o QR Code com o WhatsApp
              </div>

              <img
                :src="
                  evolutionQr.startsWith('data:')
                    ? evolutionQr
                    : `data:image/png;base64,${evolutionQr}`
                "
                alt="QR Code WhatsApp"
                class="w-64 h-64 p-2 bg-white border rounded-lg border-n-weak"
              />

              <p
                class="mt-3 text-sm text-n-slate-11"
              >
                Aguardando conexão...
              </p>
            </div>
          </template>

          <!-- API OFICIAL -->
          <div
            v-else-if="
              company.ai?.whatsapp_provider ===
              'official'
            "
            class="p-4 mt-5 border rounded-lg border-n-weak bg-n-alpha-1"
          >
            <div
              class="font-medium text-n-slate-12"
            >
              API Oficial do WhatsApp
            </div>

            <p
              class="mt-1 text-sm text-n-slate-11"
            >
              A conexão oficial é gerenciada pelas
              Inboxes WhatsApp Cloud API desta empresa.
            </p>

            <button
              type="button"
              class="px-4 py-2 mt-4 text-sm font-medium border rounded-lg border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
              @click="openAiSettings"
            >
              Gerenciar API Oficial
            </button>
          </div>
        </div>

        <!-- INSTAGRAM / FACEBOOK -->
        <div
          class="p-6 mb-5 border rounded-xl border-n-weak bg-n-background"
        >
          <h2
            class="text-lg font-semibold text-n-slate-12"
          >
            Outros canais
          </h2>

          <p
            class="mt-1 text-sm text-n-slate-11"
          >
            Canais oficiais que poderão ser conectados à mesma IA.
          </p>

          <div
            class="grid grid-cols-1 gap-4 mt-6 md:grid-cols-2"
          >
            <div
              class="p-4 border rounded-lg border-n-weak bg-n-alpha-1"
            >
              <div
                class="flex items-start justify-between gap-3"
              >
                <div>
                  <div
                    class="font-medium text-n-slate-12"
                  >
                    Instagram
                  </div>

                  <div
                    class="mt-1 text-sm text-n-slate-11"
                  >
                    Mensagens Direct da conta profissional.
                  </div>
                </div>

                <span
                  class="px-2 py-1 text-xs rounded bg-n-alpha-2 text-n-slate-11"
                >
                  Não configurado
                </span>
              </div>

              <button
                type="button"
                class="px-4 py-2 mt-4 text-sm font-medium border rounded-lg border-n-weak text-n-slate-12 opacity-60 cursor-not-allowed"
                disabled
              >
                Conectar Instagram
              </button>

              <p
                class="mt-2 text-xs text-n-slate-10"
              >
                Será habilitado após configurar a integração Meta.
              </p>
            </div>

            <div
              class="p-4 border rounded-lg border-n-weak bg-n-alpha-1"
            >
              <div
                class="flex items-start justify-between gap-3"
              >
                <div>
                  <div
                    class="font-medium text-n-slate-12"
                  >
                    Facebook Messenger
                  </div>

                  <div
                    class="mt-1 text-sm text-n-slate-11"
                  >
                    Mensagens recebidas pela Página do Facebook.
                  </div>
                </div>

                <span
                  class="px-2 py-1 text-xs rounded bg-n-alpha-2 text-n-slate-11"
                >
                  Não configurado
                </span>
              </div>

              <button
                type="button"
                class="px-4 py-2 mt-4 text-sm font-medium border rounded-lg border-n-weak text-n-slate-12 opacity-60 cursor-not-allowed"
                disabled
              >
                Conectar Facebook
              </button>

              <p
                class="mt-2 text-xs text-n-slate-10"
              >
                Será habilitado após configurar a integração Meta.
              </p>
            </div>
          </div>
        </div>

        <!-- IA -->
        <div
          class="p-6 mb-5 border rounded-xl border-n-weak bg-n-background"
        >
          <h2
            class="text-lg font-semibold text-n-slate-12"
          >
            Inteligência Artificial
          </h2>

          <p
            class="mt-1 text-sm text-n-slate-11"
          >
            Configure a IA desta empresa sem precisar entrar na Account do cliente.
          </p>

          <!-- STATUS OPERACIONAL DA IA -->
          <div
            class="p-5 mt-6 border rounded-xl border-n-weak bg-n-alpha-1"
          >
            <div
              class="flex flex-wrap items-start justify-between gap-4"
            >
              <div class="flex items-start gap-3">
                <span
                  class="inline-block w-3 h-3 mt-1.5 rounded-full shrink-0"
                  :class="{
                    'bg-n-teal-9':
                      aiOperationalStatus ===
                      'active',

                    'bg-n-ruby-9':
                      aiOperationalStatus ===
                      'paused_limit',

                    'bg-n-amber-9':
                      aiOperationalStatus ===
                      'limit_alert',

                    'bg-n-slate-7':
                      aiOperationalStatus ===
                      'disabled',
                  }"
                />

                <div>
                  <div
                    class="text-base font-semibold"
                    :class="{
                      'text-n-teal-11':
                        aiOperationalStatus ===
                        'active',

                      'text-n-ruby-11':
                        aiOperationalStatus ===
                        'paused_limit',

                      'text-n-amber-11':
                        aiOperationalStatus ===
                        'limit_alert',

                      'text-n-slate-11':
                        aiOperationalStatus ===
                        'disabled',
                    }"
                  >
                    {{ aiOperationalLabel }}
                  </div>

                  <div
                    class="mt-1 text-sm text-n-slate-11"
                  >
                    {{
                      aiOperationalDescription
                    }}
                  </div>
                </div>
              </div>

              <div
                class="px-3 py-1.5 text-xs font-medium border rounded-full border-n-weak"
              >
                <template
                  v-if="
                    aiOperationalStatus ===
                    'paused_limit'
                  "
                >
                  PAUSADA
                </template>

                <template
                  v-else-if="
                    aiOperationalStatus ===
                    'disabled'
                  "
                >
                  DESATIVADA
                </template>

                <template v-else>
                  ATIVA
                </template>
              </div>
            </div>

            <!-- AUTOMACAO LOCAL METRICAS -->


            <div


              class="grid grid-cols-1 gap-4 pt-5 mt-5 border-t md:grid-cols-2 xl:grid-cols-4 border-n-weak"


            >


              <div>


                <div class="text-xs text-n-slate-10">


                  Chamadas IA


                </div>


                <div class="mt-1 text-lg font-semibold text-n-slate-12">


                  {{ formatNumber(localUsage.ai_month_requests) }}


                </div>


              </div>



              <div>


                <div class="text-xs text-n-slate-10">


                  Respostas locais


                </div>


                <div class="mt-1 text-lg font-semibold text-n-teal-11">


                  {{ formatNumber(localUsage.month.responses) }}


                </div>


              </div>



              <div>


                <div class="text-xs text-n-slate-10">


                  Total automatizado


                </div>


                <div class="mt-1 text-lg font-semibold text-n-slate-12">


                  {{ formatNumber(localUsage.total_automated) }}


                </div>


              </div>



              <div>


                <div class="text-xs text-n-slate-10">


                  Sem usar IA


                </div>


                <div class="mt-1 text-lg font-semibold text-n-teal-11">


                  {{ Number(localUsage.local_percentage || 0).toFixed(1) }}%


                </div>


              </div>


            </div>



            <div
              v-if="
                Number(
                  aiSettings.monthly_cost_limit_brl
                ) > 0
              "
              class="grid grid-cols-1 gap-4 pt-5 mt-5 border-t md:grid-cols-3 border-n-weak"
            >
              <div>
                <div
                  class="text-xs text-n-slate-10"
                >
                  Consumo do mês
                </div>

                <div
                  class="mt-1 font-semibold text-n-slate-12"
                >
                  {{
                    formatBRL(
                      companyUsage.month.cost_brl
                    )
                  }}
                </div>
              </div>

              <div>
                <div
                  class="text-xs text-n-slate-10"
                >
                  Limite mensal
                </div>

                <div
                  class="mt-1 font-semibold text-n-slate-12"
                >
                  {{
                    formatBRL(
                      aiSettings.monthly_cost_limit_brl
                    )
                  }}
                </div>
              </div>

              <div>
                <div
                  class="text-xs text-n-slate-10"
                >
                  Utilização
                </div>

                <div
                  class="mt-1 font-semibold"
                  :class="
                    isMonthlyLimitReached
                      ? 'text-n-ruby-11'
                      : 'text-n-slate-12'
                  "
                >
                  {{
                    monthlyLimitPercent.toFixed(1)
                  }}%
                </div>
              </div>
            </div>

            <div
              v-if="
                aiOperationalStatus ===
                'paused_limit'
              "
              class="p-3 mt-5 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11"
            >
              O bot não fará novas chamadas à IA
              enquanto o consumo deste mês permanecer
              acima do limite configurado.

              Para reativar, aumente o limite mensal,
              altere a ação para “Somente avisar” ou
              aguarde o início do próximo mês.
            </div>
          </div>

          <div
            v-if="aiError"
            class="p-3 mt-5 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11"
          >
            {{ aiError }}
          </div>

          <div
            v-if="aiSuccess"
            class="p-3 mt-5 text-sm rounded-lg bg-n-teal-3 text-n-teal-11"
          >
            {{ aiSuccess }}
          </div>

          <div class="mt-6">
            <label class="flex items-center gap-3">
              <input
                v-model="aiSettings.enabled"
                type="checkbox"
              />
              <span class="font-medium text-n-slate-12">
                IA ativada
              </span>
            </label>
          </div>

          <div
            class="grid grid-cols-1 gap-4 mt-6 md:grid-cols-2"
          >
            <div>
              <label
                class="block mb-1 text-sm font-medium text-n-slate-12"
              >
                Nome da assistente
              </label>

              <input
                v-model="aiSettings.assistant_name"
                type="text"
                placeholder="Ex.: Assistente Lan Telecom"
                class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
              />
            </div>

            <div>
              <label
                class="block mb-1 text-sm font-medium text-n-slate-12"
              >
                Modelo
              </label>

              <select
                v-model="aiSettings.model"
                class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
              >
                <option value="gpt-5.6-luna">
                  GPT-5.6 Luna
                </option>

                <option value="gpt-5.6">
                  GPT-5.6
                </option>
              </select>
            </div>
          </div>

          <div class="mt-7">
            <h3 class="font-medium text-n-slate-12">
              Inboxes onde a IA responde
            </h3>

            <p class="mt-1 text-sm text-n-slate-11">
              Selecione os canais autorizados para atendimento automático.
            </p>

            <div
              v-if="!companyInboxes.length"
              class="mt-4 text-sm text-n-slate-11"
            >
              Nenhuma Inbox disponível nesta empresa.
            </div>

            <div
              v-else
              class="grid grid-cols-1 gap-3 mt-4 md:grid-cols-2"
            >
              <label
                v-for="inbox in companyInboxes"
                :key="inbox.id"
                class="flex items-center gap-3 p-3 border rounded-lg border-n-weak"
              >
                <input
                  v-model="aiSettings.allowed_inbox_ids"
                  type="checkbox"
                  :value="Number(inbox.id)"
                />

                <div>
                  <div class="font-medium text-n-slate-12">
                    {{ inbox.name }}
                  </div>

                  <div class="text-xs text-n-slate-10">
                    {{ inbox.channel_type || 'Inbox' }}
                  </div>
                </div>
              </label>
            </div>
          </div>

          <div class="mt-7">
            <h3 class="font-medium text-n-slate-12">
              Times por setor
            </h3>

            <p class="mt-1 text-sm text-n-slate-11">
              Defina qual time recebe cada tipo de atendimento.
            </p>

            <div
              class="grid grid-cols-1 gap-4 mt-4 md:grid-cols-2"
            >
              <div>
                <label class="block mb-1 text-sm font-medium text-n-slate-12">
                  Comercial
                </label>
                <select
                  v-model="aiSettings.commercial_team_id"
                  class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                >
                  <option :value="null">Não definido</option>
                  <option
                    v-for="team in companyTeams"
                    :key="team.id"
                    :value="team.id"
                  >
                    {{ team.name }}
                  </option>
                </select>
              </div>

              <div>
                <label class="block mb-1 text-sm font-medium text-n-slate-12">
                  Suporte
                </label>
                <select
                  v-model="aiSettings.support_team_id"
                  class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                >
                  <option :value="null">Não definido</option>
                  <option
                    v-for="team in companyTeams"
                    :key="team.id"
                    :value="team.id"
                  >
                    {{ team.name }}
                  </option>
                </select>
              </div>

              <div>
                <label class="block mb-1 text-sm font-medium text-n-slate-12">
                  Financeiro
                </label>
                <select
                  v-model="aiSettings.financial_team_id"
                  class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                >
                  <option :value="null">Não definido</option>
                  <option
                    v-for="team in companyTeams"
                    :key="team.id"
                    :value="team.id"
                  >
                    {{ team.name }}
                  </option>
                </select>
              </div>

              <div>
                <label class="block mb-1 text-sm font-medium text-n-slate-12">
                  Cancelamento
                </label>
                <select
                  v-model="aiSettings.cancellation_team_id"
                  class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                >
                  <option :value="null">Não definido</option>
                  <option
                    v-for="team in companyTeams"
                    :key="team.id"
                    :value="team.id"
                  >
                    {{ team.name }}
                  </option>
                </select>
              </div>

              <div>
                <label class="block mb-1 text-sm font-medium text-n-slate-12">
                  Atendimento geral
                </label>
                <select
                  v-model="aiSettings.general_team_id"
                  class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                >
                  <option :value="null">Não definido</option>
                  <option
                    v-for="team in companyTeams"
                    :key="team.id"
                    :value="team.id"
                  >
                    {{ team.name }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <div
            class="p-5 mt-8 border rounded-xl border-n-weak bg-n-alpha-1"
          >
            <h3 class="text-lg font-semibold text-n-slate-12">
              Perfil de consumo
            </h3>

            <p class="mt-1 text-sm text-n-slate-11">
              Controle quanto histórico e quantos tokens de resposta esta empresa pode usar.
            </p>

            <div
              class="grid grid-cols-1 gap-3 mt-6 md:grid-cols-4"
            >
              <label
                class="p-4 border rounded-lg cursor-pointer border-n-weak"
              >
                <div class="flex items-start gap-3">
                  <input
                    v-model="aiSettings.usage_profile"
                    type="radio"
                    value="economy"
                    class="mt-1"
                    @change="applyUsageProfile('economy')"
                  />

                  <div>
                    <div class="font-medium text-n-slate-12">
                      Econômico
                    </div>
                    <div class="mt-1 text-sm text-n-slate-11">
                      6 mensagens
                    </div>
                    <div class="text-sm text-n-slate-11">
                      300 tokens
                    </div>
                  </div>
                </div>
              </label>

              <label
                class="p-4 border rounded-lg cursor-pointer border-n-weak"
              >
                <div class="flex items-start gap-3">
                  <input
                    v-model="aiSettings.usage_profile"
                    type="radio"
                    value="balanced"
                    class="mt-1"
                    @change="applyUsageProfile('balanced')"
                  />

                  <div>
                    <div class="font-medium text-n-slate-12">
                      Equilibrado
                    </div>
                    <div class="mt-1 text-sm text-n-slate-11">
                      15 mensagens
                    </div>
                    <div class="text-sm text-n-slate-11">
                      700 tokens
                    </div>
                  </div>
                </div>
              </label>

              <label
                class="p-4 border rounded-lg cursor-pointer border-n-weak"
              >
                <div class="flex items-start gap-3">
                  <input
                    v-model="aiSettings.usage_profile"
                    type="radio"
                    value="advanced"
                    class="mt-1"
                    @change="applyUsageProfile('advanced')"
                  />

                  <div>
                    <div class="font-medium text-n-slate-12">
                      Avançado
                    </div>
                    <div class="mt-1 text-sm text-n-slate-11">
                      30 mensagens
                    </div>
                    <div class="text-sm text-n-slate-11">
                      1000 tokens
                    </div>
                  </div>
                </div>
              </label>

              <label
                class="p-4 border rounded-lg cursor-pointer border-n-weak"
              >
                <div class="flex items-start gap-3">
                  <input
                    v-model="aiSettings.usage_profile"
                    type="radio"
                    value="custom"
                    class="mt-1"
                  />

                  <div>
                    <div class="font-medium text-n-slate-12">
                      Personalizado
                    </div>
                    <div class="mt-1 text-sm text-n-slate-11">
                      Ajuste manual
                    </div>
                  </div>
                </div>
              </label>
            </div>

            <div
              class="grid grid-cols-1 gap-4 mt-6 md:grid-cols-2"
            >
              <div>
                <label
                  class="block mb-1 text-sm font-medium text-n-slate-12"
                >
                  Histórico enviado à IA
                </label>

                <input
                  v-model.number="aiSettings.history_limit"
                  type="number"
                  min="1"
                  max="50"
                  class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                  @input="markUsageCustom"
                />

                <p class="mt-1 text-xs text-n-slate-10">
                  Quantidade máxima de mensagens anteriores enviadas em cada chamada.
                </p>
              </div>

              <div>
                <label
                  class="block mb-1 text-sm font-medium text-n-slate-12"
                >
                  Máximo de tokens da resposta
                </label>

                <input
                  v-model.number="aiSettings.max_output_tokens"
                  type="number"
                  min="100"
                  max="2000"
                  step="50"
                  class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                  @input="markUsageCustom"
                />

                <p class="mt-1 text-xs text-n-slate-10">
                  Limite máximo permitido para a resposta gerada pela IA.
                </p>
              </div>
            </div>
          </div>

          <div
            class="p-5 mt-8 border rounded-xl border-n-weak bg-n-alpha-1"
          >
            <div
              class="flex flex-wrap items-start justify-between gap-4"
            >
              <div>
                <h3
                  class="text-lg font-semibold text-n-slate-12"
                >
                  Uso da IA
                </h3>

                <p
                  class="mt-1 text-sm text-n-slate-11"
                >
                  Acompanhe o consumo de inteligência artificial desta empresa.
                </p>
              </div>

              <button
                type="button"
                class="px-3 py-2 text-sm border rounded-lg border-n-weak text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-50"
                :disabled="usageLoading"
                @click="loadCompanyUsage"
              >
                {{
                  usageLoading
                    ? 'Atualizando...'
                    : 'Atualizar consumo'
                }}
              </button>
            </div>

            <div
              class="grid grid-cols-1 gap-4 mt-6 md:grid-cols-3"
            >
              <div
                class="p-4 border rounded-lg border-n-weak bg-n-background"
              >
                <div
                  class="text-sm text-n-slate-10"
                >
                  Hoje
                </div>

                <div
                  class="mt-2 text-xl font-semibold text-n-slate-12"
                >
                  {{
                    formatBRL(
                      companyUsage.today.cost_brl
                    )
                  }}
                </div>

                <div
                  class="mt-1 text-sm text-n-slate-11"
                >
                  {{
                    formatNumber(
                      companyUsage.today.requests
                    )
                  }}
                  requisição(ões)
                </div>
              </div>

              <div
                class="p-4 border rounded-lg border-n-weak bg-n-background"
              >
                <div
                  class="text-sm text-n-slate-10"
                >
                  Consumo neste mês
                </div>

                <div
                  class="mt-2 text-xl font-semibold text-n-slate-12"
                >
                  {{
                    formatBRL(
                      companyUsage.month.cost_brl
                    )
                  }}
                </div>

                <div
                  class="mt-1 text-sm text-n-slate-11"
                >
                  {{
                    formatNumber(
                      companyUsage.month.requests
                    )
                  }}
                  requisição(ões)
                </div>
              </div>

              <div
                class="p-4 border rounded-lg border-n-weak bg-n-background"
              >
                <div
                  class="text-sm text-n-slate-10"
                >
                  Total acumulado
                </div>

                <div
                  class="mt-2 text-xl font-semibold text-n-slate-12"
                >
                  {{
                    formatBRL(
                      companyUsage.total.cost_brl
                    )
                  }}
                </div>

                <div
                  class="mt-1 text-sm text-n-slate-11"
                >
                  {{
                    formatNumber(
                      companyUsage.total.requests
                    )
                  }}
                  requisição(ões)
                </div>
              </div>
            </div>

            <div
              class="grid grid-cols-1 gap-4 mt-4 md:grid-cols-3"
            >
              <div
                class="p-4 border rounded-lg border-n-weak"
              >
                <div class="text-sm text-n-slate-10">
                  Tokens de entrada
                </div>

                <div class="mt-1 font-semibold text-n-slate-12">
                  {{
                    formatNumber(
                      companyUsage.month.input_tokens
                    )
                  }}
                </div>
              </div>

              <div
                class="p-4 border rounded-lg border-n-weak"
              >
                <div class="text-sm text-n-slate-10">
                  Tokens em cache
                </div>

                <div class="mt-1 font-semibold text-n-slate-12">
                  {{
                    formatNumber(
                      companyUsage.month.cached_input_tokens
                    )
                  }}
                </div>
              </div>

              <div
                class="p-4 border rounded-lg border-n-weak"
              >
                <div class="text-sm text-n-slate-10">
                  Tokens de saída
                </div>

                <div class="mt-1 font-semibold text-n-slate-12">
                  {{
                    formatNumber(
                      companyUsage.month.output_tokens
                    )
                  }}
                </div>
              </div>
            </div>

            <div
              class="grid grid-cols-1 gap-4 mt-4 md:grid-cols-3"
            >
              <div
                class="p-4 border rounded-lg border-n-weak"
              >
                <div class="text-sm text-n-slate-10">
                  Custo médio hoje
                </div>

                <div
                  class="mt-1 font-semibold text-n-slate-12"
                >
                  {{
                    formatBRL(
                      averageCost(
                        companyUsage.today.cost_brl,
                        companyUsage.today.requests
                      )
                    )
                  }}
                </div>
              </div>

              <div
                class="p-4 border rounded-lg border-n-weak"
              >
                <div class="text-sm text-n-slate-10">
                  Custo médio no mês
                </div>

                <div
                  class="mt-1 font-semibold text-n-slate-12"
                >
                  {{
                    formatBRL(
                      averageCost(
                        companyUsage.month.cost_brl,
                        companyUsage.month.requests
                      )
                    )
                  }}
                </div>
              </div>

              <div
                class="p-4 border rounded-lg border-n-weak"
              >
                <div class="text-sm text-n-slate-10">
                  Custo médio acumulado
                </div>

                <div
                  class="mt-1 font-semibold text-n-slate-12"
                >
                  {{
                    formatBRL(
                      averageCost(
                        companyUsage.total.cost_brl,
                        companyUsage.total.requests
                      )
                    )
                  }}
                </div>
              </div>
            </div>

            <div
              class="p-5 mt-5 border rounded-lg border-n-weak bg-n-background"
            >
              <div
                class="grid grid-cols-1 gap-4 md:grid-cols-2"
              >
                <div>
                  <label
                    class="block mb-1 text-sm font-medium text-n-slate-12"
                  >
                    Limite mensal de IA
                  </label>

                  <div
                    class="flex items-center gap-2"
                  >
                    <span class="text-n-slate-11">
                      R$
                    </span>

                    <input
                      v-model.number="
                        aiSettings.monthly_cost_limit_brl
                      "
                      type="number"
                      min="0"
                      step="1"
                      class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                    />
                  </div>

                  <p
                    class="mt-1 text-xs text-n-slate-10"
                  >
                    Use 0 para não definir limite mensal.
                  </p>

                  <div class="mt-5">
                    <div
                      class="mb-2 text-sm font-medium text-n-slate-12"
                    >
                      Ação ao atingir o limite
                    </div>

                    <div class="space-y-3">
                      <label
                        class="flex items-start gap-3 p-3 border rounded-lg cursor-pointer border-n-weak"
                      >
                        <input
                          v-model="aiSettings.monthly_limit_action"
                          type="radio"
                          value="alert"
                          class="mt-1"
                        />

                        <div>
                          <div
                            class="font-medium text-n-slate-12"
                          >
                            Somente avisar
                          </div>

                          <div
                            class="mt-1 text-xs text-n-slate-10"
                          >
                            Mostra o alerta, mas mantém a IA funcionando.
                          </div>
                        </div>
                      </label>

                      <label
                        class="flex items-start gap-3 p-3 border rounded-lg cursor-pointer border-n-weak"
                      >
                        <input
                          v-model="aiSettings.monthly_limit_action"
                          type="radio"
                          value="pause"
                          class="mt-1"
                        />

                        <div>
                          <div
                            class="font-medium text-n-slate-12"
                          >
                            Pausar IA automaticamente
                          </div>

                          <div
                            class="mt-1 text-xs text-n-slate-10"
                          >
                            Impede novas chamadas de IA quando o limite mensal for atingido.
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                <div
                  v-if="
                    Number(
                      aiSettings.monthly_cost_limit_brl
                    ) > 0
                  "
                >
                  <div
                    class="text-sm text-n-slate-10"
                  >
                    Restante no mês
                  </div>

                  <div
                    class="mt-1 text-xl font-semibold text-n-slate-12"
                  >
                    {{
                      formatBRL(
                        monthlyLimitRemaining
                      )
                    }}
                  </div>

                  <div
                    class="mt-1 text-sm text-n-slate-11"
                  >
                    {{
                      monthlyLimitPercent.toFixed(1)
                    }}%
                    utilizado
                  </div>
                </div>
              </div>

              <div
                v-if="
                  Number(
                    aiSettings.monthly_cost_limit_brl
                  ) > 0
                "
                class="mt-5"
              >
                <div
                  class="w-full h-2 overflow-hidden rounded-full bg-n-alpha-2"
                >
                  <div
                    class="h-full rounded-full bg-n-brand"
                    :style="{
                      width:
                        `${monthlyLimitPercent}%`,
                    }"
                  />
                </div>

                <div
                  v-if="monthlyLimitPercent >= 100"
                  class="p-3 mt-4 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11"
                >
                  Limite mensal de IA atingido.
                  A IA continua funcionando; este limite
                  está configurado apenas como alerta.
                </div>

                <div
                  v-else-if="monthlyLimitPercent >= 80"
                  class="p-3 mt-4 text-sm rounded-lg bg-n-amber-3 text-n-amber-11"
                >
                  Esta empresa já utilizou mais de 80%
                  do limite mensal configurado.
                </div>
              </div>
            </div>

            <div
              class="flex flex-wrap gap-6 mt-4 text-sm text-n-slate-11"
            >
              <div>
                Mês:
                <strong class="text-n-slate-12">
                  {{
                    companyUsage.current_month ||
                    '-'
                  }}
                </strong>
              </div>

              <div>
                Cotação USD/BRL:
                <strong class="text-n-slate-12">
                  R$
                  {{
                    Number(
                      companyUsage.usd_brl_rate ||
                      0
                    ).toFixed(4)
                  }}
                </strong>
              </div>
            </div>
          </div>

          <div
            class="p-5 mt-8 border rounded-xl border-n-weak bg-n-alpha-1"
          >
            <h3
              class="text-lg font-semibold text-n-slate-12"
            >
              Comportamento da IA
            </h3>

            <p
              class="mt-1 text-sm text-n-slate-11"
            >
              Defina como a assistente deve agir e quando transferir o atendimento para uma pessoa.
            </p>

            <div class="mt-6 space-y-5">

              <label
                class="flex items-center justify-between gap-4 p-3 border rounded-lg border-n-weak"
              >
                <div>
                  <div
                    class="font-medium text-n-slate-12"
                  >
                    Transferir automaticamente
                  </div>

                  <div
                    class="mt-1 text-sm text-n-slate-11"
                  >
                    Permite que a IA transfira a conversa para o time adequado.
                  </div>
                </div>

                <input
                  v-model="aiSettings.auto_transfer"
                  type="checkbox"
                  class="w-5 h-5"
                />
              </label>

              <label
                class="flex items-center justify-between gap-4 p-3 border rounded-lg border-n-weak"
              >
                <div>
                  <div
                    class="font-medium text-n-slate-12"
                  >
                    Adicionar etiqueta aguardando-atendente
                  </div>

                  <div
                    class="mt-1 text-sm text-n-slate-11"
                  >
                    Marca a conversa quando ela estiver aguardando atendimento humano.
                  </div>
                </div>

                <input
                  v-model="aiSettings.add_waiting_label"
                  type="checkbox"
                  class="w-5 h-5"
                />
              </label>

              <label
                class="flex items-center justify-between gap-4 p-3 border rounded-lg border-n-weak"
              >
                <div>
                  <div
                    class="font-medium text-n-slate-12"
                  >
                    Parar IA após transferir
                  </div>

                  <div
                    class="mt-1 text-sm text-n-slate-11"
                  >
                    Impede novas respostas automáticas depois que um atendente assumir a conversa.
                  </div>
                </div>

                <input
                  v-model="aiSettings.stop_ai_after_transfer"
                  type="checkbox"
                  class="w-5 h-5"
                />
              </label>

            </div>

            <div class="mt-6">
              <label
                class="block mb-1 text-sm font-medium text-n-slate-12"
              >
                Mensagem de transferência
              </label>

              <p
                class="mb-2 text-sm text-n-slate-11"
              >
                Mensagem enviada ao cliente antes de encaminhar a conversa para um atendente.
              </p>

              <textarea
                v-model="aiSettings.transfer_message"
                rows="4"
                placeholder="Ex.: Vou encaminhar seu atendimento para um de nossos atendentes."
                class="w-full px-3 py-3 border rounded-lg resize-y border-n-weak bg-n-background text-n-slate-12"
              />
            </div>
          </div>

                                              <div
              class="p-5 mt-5 border rounded-xl border-n-weak bg-n-alpha-1"
            >
              <div
                class="flex flex-wrap items-start justify-between gap-4"
              >
                <div
                    class="p-5 mb-6 border rounded-xl border-n-weak bg-n-background"
                  >
                                        <div
                      class="p-5 border rounded-xl border-n-weak bg-n-background"
                    >
                      <div
                        class="flex flex-wrap items-center justify-between gap-3"
                      >
                        <div>
                          <h3
                            class="text-base font-semibold text-n-slate-12"
                          >
                            Histórico operacional
                          </h3>

                          <p
                            class="mt-1 text-sm text-n-slate-10"
                          >
                            Eventos importantes desta empresa.
                          </p>
                        </div>

                        <button
                          type="button"
                          class="px-3 py-2 text-sm border rounded-lg border-n-weak text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-50"
                          :disabled="operationalHistoryLoading"
                          @click="loadOperationalHistory"
                        >
                          {{
                            operationalHistoryLoading
                              ? 'Atualizando...'
                              : 'Atualizar histórico'
                          }}
                        </button>
                      </div>

                      <div
                        v-if="operationalHistoryError"
                        class="p-3 mt-4 text-sm border rounded-lg border-n-ruby-6 bg-n-ruby-3 text-n-ruby-11"
                      >
                        {{ operationalHistoryError }}
                      </div>

                      <div
                        v-else-if="
                          operationalHistoryLoading &&
                          !operationalHistory.length
                        "
                        class="py-8 text-sm text-center text-n-slate-10"
                      >
                        Carregando histórico...
                      </div>

                      <div
                        v-else-if="!operationalHistory.length"
                        class="py-8 text-sm text-center text-n-slate-10"
                      >
                        Nenhum evento operacional registrado.
                      </div>

                      <div
                        v-else
                        class="mt-5 space-y-3"
                      >
                        <div
                          v-for="event in operationalHistory"
                          :key="event.id"
                          class="p-4 border rounded-lg border-n-weak bg-n-alpha-1"
                        >
                          <div
                            class="flex flex-wrap items-start justify-between gap-3"
                          >
                            <div class="min-w-0">
                              <div
                                class="flex flex-wrap items-center gap-2"
                              >
                                <span
                                  class="inline-flex items-center px-2 py-1 text-xs font-medium border rounded-full"
                                  :class="
                                    operationalEventClass(
                                      event.severity
                                    )
                                  "
                                >
                                  {{
                                    operationalEventSeverityLabel(
                                      event.severity
                                    )
                                  }}
                                </span>

                                <span
                                  class="text-xs text-n-slate-9"
                                >
                                  {{ event.event_type }}
                                </span>
                              </div>

                              <div
                                class="mt-2 text-sm font-medium text-n-slate-12"
                              >
                                {{ event.title }}
                              </div>

                              <div
                                v-if="event.description"
                                class="mt-1 text-sm text-n-slate-10"
                              >
                                {{ event.description }}
                              </div>
                            </div>

                            <div
                              class="text-xs whitespace-nowrap text-n-slate-9"
                            >
                              {{
                                formatOperationalEventDate(
                                  event.occurred_at
                                )
                              }}
                            </div>
                          </div>

                          <details
                            v-if="
                              event.metadata &&
                              Object.keys(event.metadata).length
                            "
                            class="mt-3"
                          >
                            <summary
                              class="text-xs cursor-pointer text-n-brand"
                            >
                              Ver detalhes técnicos
                            </summary>

                            <pre
                              class="p-3 mt-2 overflow-auto text-xs border rounded-lg border-n-weak bg-n-background text-n-slate-10"
                            >{{ JSON.stringify(event.metadata, null, 2) }}</pre>
                          </details>
                        </div>
                      </div>
                    </div>

<div>
                      <h4
                        class="font-medium text-n-slate-12"
                      >
                        Dados da empresa para respostas
                      </h4>

                      <p
                        class="mt-1 text-xs text-n-slate-10"
                      >
                        Cadastre uma vez e use as variáveis
                        em qualquer resposta local.
                      </p>
                    </div>

                    <div
                      class="grid grid-cols-1 gap-4 mt-5 md:grid-cols-2"
                    >
                      <div>
                        <label
                          class="block mb-1 text-xs font-medium text-n-slate-10"
                        >
                          Nome da empresa
                        </label>

                        <input
                          v-model="aiSettings.company_variables.company_name"
                          type="text"
                          class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                        />

                        <div
                          class="mt-1 text-xs text-n-slate-10"
                        >
                          {empresa}
                        </div>
                      </div>

                      <div>
                        <label
                          class="block mb-1 text-xs font-medium text-n-slate-10"
                        >
                          Telefone
                        </label>

                        <input
                          v-model="aiSettings.company_variables.phone"
                          type="text"
                          class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                        />

                        <div
                          class="mt-1 text-xs text-n-slate-10"
                        >
                          {telefone_empresa}
                        </div>
                      </div>

                      <div>
                        <label
                          class="block mb-1 text-xs font-medium text-n-slate-10"
                        >
                          WhatsApp
                        </label>

                        <input
                          v-model="aiSettings.company_variables.whatsapp"
                          type="text"
                          class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                        />

                        <div
                          class="mt-1 text-xs text-n-slate-10"
                        >
                          {whatsapp_empresa}
                        </div>
                      </div>

                      <div>
                        <label
                          class="block mb-1 text-xs font-medium text-n-slate-10"
                        >
                          E-mail
                        </label>

                        <input
                          v-model="aiSettings.company_variables.email"
                          type="text"
                          class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                        />

                        <div
                          class="mt-1 text-xs text-n-slate-10"
                        >
                          {email_empresa}
                        </div>
                      </div>

                      <div>
                        <label
                          class="block mb-1 text-xs font-medium text-n-slate-10"
                        >
                          Site
                        </label>

                        <input
                          v-model="aiSettings.company_variables.site"
                          type="text"
                          class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                        />

                        <div
                          class="mt-1 text-xs text-n-slate-10"
                        >
                          {site_empresa}
                        </div>
                      </div>

                      <div>
                        <label
                          class="block mb-1 text-xs font-medium text-n-slate-10"
                        >
                          Horário de atendimento
                        </label>

                        <input
                          v-model="aiSettings.company_variables.business_hours"
                          type="text"
                          placeholder="Segunda a sexta, 08h às 18h"
                          class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                        />

                        <div
                          class="mt-1 text-xs text-n-slate-10"
                        >
                          {horario_atendimento}
                        </div>
                      </div>

                      <div class="md:col-span-2">
                        <label
                          class="block mb-1 text-xs font-medium text-n-slate-10"
                        >
                          Endereço
                        </label>

                        <input
                          v-model="aiSettings.company_variables.address"
                          type="text"
                          class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                        />

                        <div
                          class="mt-1 text-xs text-n-slate-10"
                        >
                          {endereco_empresa}
                        </div>
                      </div>
                    </div>

                    <div
                      class="p-4 mt-5 border rounded-xl border-n-weak bg-n-alpha-1"
                    >
                      <div
                        class="flex flex-wrap items-center justify-between gap-3"
                      >
                        <div>
                          <h5
                            class="text-sm font-medium text-n-slate-12"
                          >
                            Variáveis personalizadas
                          </h5>

                          <p
                            class="mt-1 text-xs text-n-slate-10"
                          >
                            Crie dados próprios desta empresa e use
                            nas respostas locais.
                          </p>
                        </div>

                        <button
                          type="button"
                          class="px-3 py-2 text-sm font-medium border rounded-lg border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
                          @click="addCustomCompanyVariable"
                        >
                          + Nova variável
                        </button>
                      </div>

                      <div
                        v-if="!customCompanyVariableEntries.length"
                        class="p-4 mt-4 text-sm text-center border border-dashed rounded-lg border-n-weak text-n-slate-10"
                      >
                        Nenhuma variável personalizada cadastrada.
                      </div>

                      <div
                        v-else
                        class="mt-4 space-y-3"
                      >
                        <div
                          v-for="item in customCompanyVariableEntries"
                          :key="item.storageKey"
                          class="grid grid-cols-1 gap-3 p-3 border rounded-lg border-n-weak md:grid-cols-[220px_1fr_auto]"
                        >
                          <div>
                            <label
                              class="block mb-1 text-xs font-medium text-n-slate-10"
                            >
                              Variável
                            </label>

                            <div
                              class="flex items-center"
                            >
                              <span
                                class="px-2 py-2 font-mono text-sm border border-r-0 rounded-l-lg border-n-weak bg-n-alpha-2 text-n-slate-10"
                              >
                                {
                              </span>

                              <input
                                :value="item.key"
                                type="text"
                                class="w-full px-2 py-2 font-mono text-sm border border-n-weak bg-n-background text-n-slate-12"
                                placeholder="pix"
                                @change="
                                  renameCustomCompanyVariable(
                                    item.storageKey,
                                    $event.target.value
                                  )
                                "
                              />

                              <span
                                class="px-2 py-2 font-mono text-sm border border-l-0 rounded-r-lg border-n-weak bg-n-alpha-2 text-n-slate-10"
                              >
                                }
                              </span>
                            </div>
                          </div>

                          <div>
                            <label
                              class="block mb-1 text-xs font-medium text-n-slate-10"
                            >
                              Valor
                            </label>

                            <input
                              :value="item.value"
                              type="text"
                              class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                              placeholder="Valor usado quando a variável aparecer na resposta"
                              @input="
                                setCustomCompanyVariableValue(
                                  item.storageKey,
                                  $event.target.value
                                )
                              "
                            />
                          </div>

                          <div
                            class="flex items-end"
                          >
                            <button
                              type="button"
                              class="px-3 py-2 text-sm border rounded-lg border-n-ruby-7 text-n-ruby-11 hover:bg-n-ruby-3"
                              @click="
                                removeCustomCompanyVariable(
                                  item
                                )
                              "
                            >
                              Excluir
                            </button>
                          </div>
                        </div>
                      </div>

                      <p
                        class="mt-3 text-xs text-n-slate-10"
                      >
                        Use apenas letras, números e _. Espaços e
                        acentos serão convertidos automaticamente.
                      </p>
                    </div>

                    <div
                      class="p-3 mt-5 text-xs border rounded-lg border-n-weak bg-n-alpha-2 text-n-slate-11"
                    >
                      Também disponíveis:
                      <strong>{assistente}</strong>
                      e
                      <strong>{saudacao}</strong>.
                    </div>
                  </div>

                  
                <div
                  class="p-5 mb-5 border rounded-xl border-n-weak bg-n-background"
                >
                  <div
                    class="flex flex-wrap items-start justify-between gap-3"
                  >
                    
                <div
                  class="p-5 mb-5 border rounded-xl border-n-weak bg-n-background"
                >
                  <div
                    class="flex flex-wrap items-start justify-between gap-4"
                  >
                    <div>
                      <h4
                        class="text-base font-semibold text-n-slate-12"
                      >
                        Visão geral da automação
                      </h4>

                      <p
                        class="mt-1 text-sm text-n-slate-10"
                      >
                        Desempenho da automação desta empresa
                        no mês atual.
                      </p>
                    </div>

                    <span
                      v-if="
                        automationOverview.status ===
                        'healthy'
                      "
                      class="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-full bg-n-teal-3 text-n-teal-11"
                    >
                      <span>●</span>
                      {{ automationOverview.statusLabel }}
                    </span>

                    <span
                      v-else-if="
                        automationOverview.status ===
                        'warning'
                      "
                      class="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-full bg-n-amber-3 text-n-amber-11"
                    >
                      <span>●</span>
                      {{ automationOverview.statusLabel }}
                    </span>

                    <span
                      v-else-if="
                        automationOverview.status ===
                        'critical'
                      "
                      class="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-full bg-n-ruby-3 text-n-ruby-11"
                    >
                      <span>●</span>
                      {{ automationOverview.statusLabel }}
                    </span>

                    <span
                      v-else
                      class="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-full bg-n-alpha-3 text-n-slate-11"
                    >
                      <span>●</span>
                      {{ automationOverview.statusLabel }}
                    </span>
                  </div>


                  <!-- MÉTRICAS PRINCIPAIS -->
                  <div
                    class="grid grid-cols-1 gap-3 mt-5 sm:grid-cols-2 xl:grid-cols-4"
                  >
                    <div
                      class="p-4 border rounded-xl border-n-weak bg-n-alpha-1"
                    >
                      <p
                        class="text-xs font-medium text-n-slate-10"
                      >
                        Total automatizado
                      </p>

                      <p
                        class="mt-2 text-2xl font-semibold text-n-slate-12"
                      >
                        {{
                          automationOverview
                            .totalAutomated
                            .toLocaleString('pt-BR')
                        }}
                      </p>

                      <p
                        class="mt-1 text-xs text-n-slate-10"
                      >
                        Local + chamadas de IA
                      </p>
                    </div>


                    <div
                      class="p-4 border rounded-xl border-n-weak bg-n-alpha-1"
                    >
                      <p
                        class="text-xs font-medium text-n-slate-10"
                      >
                        Resolvido sem IA
                      </p>

                      <p
                        class="mt-2 text-2xl font-semibold text-n-teal-11"
                      >
                        {{
                          automationOverview
                            .localResponses
                            .toLocaleString('pt-BR')
                        }}
                      </p>

                      <p
                        class="mt-1 text-xs text-n-slate-10"
                      >
                        Respostas locais
                      </p>
                    </div>


                    <div
                      class="p-4 border rounded-xl border-n-weak bg-n-alpha-1"
                    >
                      <p
                        class="text-xs font-medium text-n-slate-10"
                      >
                        Chamadas IA
                      </p>

                      <p
                        class="mt-2 text-2xl font-semibold text-n-slate-12"
                      >
                        {{
                          automationOverview
                            .aiRequests
                            .toLocaleString('pt-BR')
                        }}
                      </p>

                      <p
                        class="mt-1 text-xs text-n-slate-10"
                      >
                        Requisições pagas
                      </p>
                    </div>


                    <div
                      class="p-4 border rounded-xl border-n-weak bg-n-alpha-1"
                    >
                      <p
                        class="text-xs font-medium text-n-slate-10"
                      >
                        Sem usar IA
                      </p>

                      <p
                        class="mt-2 text-2xl font-semibold text-n-teal-11"
                      >
                        {{
                          formatAutomationPercent(
                            automationOverview
                              .localPercentage
                          )
                        }}%
                      </p>

                      <p
                        class="mt-1 text-xs text-n-slate-10"
                      >
                        Economia operacional
                      </p>
                    </div>
                  </div>


                  <!-- CUSTOS -->
                  <div
                    class="grid grid-cols-1 gap-3 mt-3 lg:grid-cols-3"
                  >
                    <div
                      class="p-4 border rounded-xl border-n-weak bg-n-alpha-1"
                    >
                      <p
                        class="text-xs font-medium text-n-slate-10"
                      >
                        Custo IA no mês
                      </p>

                      <p
                        class="mt-2 text-xl font-semibold text-n-slate-12"
                      >
                        {{
                          formatAutomationCurrency(
                            automationOverview.aiCost
                          )
                        }}
                      </p>
                    </div>


                    <div
                      class="p-4 border rounded-xl border-n-weak bg-n-alpha-1"
                    >
                      <p
                        class="text-xs font-medium text-n-slate-10"
                      >
                        Economia estimada
                      </p>

                      <p
                        class="mt-2 text-xl font-semibold text-n-teal-11"
                      >
                        {{
                          formatAutomationCurrency(
                            automationOverview
                              .estimatedSavings
                          )
                        }}
                      </p>

                      <p
                        class="mt-1 text-xs text-n-slate-10"
                      >
                        Custo de IA evitado pelas respostas locais
                      </p>
                    </div>


                    <div
                      class="p-4 border rounded-xl border-n-weak bg-n-alpha-1"
                    >
                      <p
                        class="text-xs font-medium text-n-slate-10"
                      >
                        Custo médio por chamada IA
                      </p>

                      <p
                        class="mt-2 text-xl font-semibold text-n-slate-12"
                      >
                        {{
                          formatAutomationCurrency(
                            automationOverview
                              .averageAiCost
                          )
                        }}
                      </p>
                    </div>
                  </div>


                  <!-- LIMITE -->
                  <div
                    class="p-4 mt-3 border rounded-xl border-n-weak bg-n-alpha-1"
                  >
                    <div
                      class="flex flex-wrap items-center justify-between gap-3"
                    >
                      <div>
                        <p
                          class="text-xs font-medium text-n-slate-10"
                        >
                          Limite mensal da IA
                        </p>

                        <p
                          v-if="
                            automationOverview
                              .monthlyLimit > 0
                          "
                          class="mt-1 text-sm font-medium text-n-slate-12"
                        >
                          {{
                            formatAutomationCurrency(
                              automationOverview.aiCost
                            )
                          }}
                          de
                          {{
                            formatAutomationCurrency(
                              automationOverview
                                .monthlyLimit
                            )
                          }}
                        </p>

                        <p
                          v-else
                          class="mt-1 text-sm text-n-slate-11"
                        >
                          Sem limite configurado
                        </p>
                      </div>

                      <div
                        v-if="
                          automationOverview
                            .monthlyLimit > 0
                        "
                        class="text-right"
                      >
                        <p
                          class="text-xl font-semibold text-n-slate-12"
                        >
                          {{
                            formatAutomationPercent(
                              automationOverview
                                .limitPercentage
                            )
                          }}%
                        </p>

                        <p
                          class="text-xs text-n-slate-10"
                        >
                          utilizado
                        </p>
                      </div>
                    </div>


                    <div
                      v-if="
                        automationOverview
                          .monthlyLimit > 0
                      "
                      class="w-full h-2 mt-4 overflow-hidden rounded-full bg-n-alpha-3"
                    >
                      <div
                        class="h-full transition-all rounded-full bg-n-brand"
                        :style="{
                          width: `${Math.min(
                            automationOverview
                              .limitPercentage,
                            100
                          )}%`,
                        }"
                      />
                    </div>


                    <div
                      v-if="
                        automationOverview
                          .monthlyLimit > 0 &&
                        automationOverview
                          .limitPercentage >= 80 &&
                        automationOverview
                          .limitPercentage < 100
                      "
                      class="p-3 mt-4 text-sm border rounded-lg border-n-amber-6 bg-n-amber-2 text-n-amber-11"
                    >
                      A empresa já utilizou mais de 80%
                      do limite mensal da IA.
                    </div>


                    <div
                      v-if="
                        automationOverview
                          .monthlyLimit > 0 &&
                        automationOverview
                          .limitPercentage >= 100
                      "
                      class="p-3 mt-4 text-sm border rounded-lg border-n-ruby-6 bg-n-ruby-2 text-n-ruby-11"
                    >
                      O limite mensal configurado foi atingido.
                    </div>
                  </div>


                  <!-- RESUMO DA ECONOMIA -->
                  <div
                    v-if="
                      automationOverview
                        .totalAutomated > 0
                    "
                    class="p-4 mt-3 border rounded-xl border-n-teal-6 bg-n-teal-2"
                  >
                    <p
                      class="text-sm font-medium text-n-teal-11"
                    >
                      Impacto das respostas locais
                    </p>

                    <p
                      class="mt-1 text-sm text-n-slate-11"
                    >
                      Neste mês,
                      <strong>
                        {{
                          automationOverview
                            .localResponses
                            .toLocaleString('pt-BR')
                        }}
                      </strong>
                      atendimento(s) foram respondidos
                      sem chamar a IA, representando
                      <strong>
                        {{
                          formatAutomationPercent(
                            automationOverview
                              .localPercentage
                          )
                        }}%
                      </strong>
                      da automação.
                    </p>

                    <p
                      v-if="
                        automationOverview.aiRequests > 0
                      "
                      class="mt-2 text-sm text-n-slate-11"
                    >
                      Considerando o custo médio das chamadas
                      realizadas neste mês, a economia estimada
                      é de
                      <strong>
                        {{
                          formatAutomationCurrency(
                            automationOverview
                              .estimatedSavings
                          )
                        }}
                      </strong>.
                    </p>
                  </div>
                </div>

<div>
                      <h4
                        class="text-base font-semibold text-n-slate-12"
                      >
                        Testar resposta local
                      </h4>

                      <p
                        class="mt-1 text-sm text-n-slate-10"
                      >
                        Simule uma mensagem antes de testar pelo WhatsApp.
                      </p>
                    </div>

                    <span
                      v-if="
                        localReplyTesterResult.status ===
                        'local'
                      "
                      class="px-3 py-1 text-xs font-medium rounded-full bg-n-teal-3 text-n-teal-11"
                    >
                      Resposta local
                    </span>

                    <span
                      v-else-if="
                        localReplyTesterResult.status ===
                        'ai'
                      "
                      class="px-3 py-1 text-xs font-medium rounded-full bg-n-amber-3 text-n-amber-11"
                    >
                      Vai para IA
                    </span>

                    <span
                      v-else-if="
                        localReplyTesterResult.status ===
                        'disabled'
                      "
                      class="px-3 py-1 text-xs font-medium rounded-full bg-n-ruby-3 text-n-ruby-11"
                    >
                      Respostas locais desativadas
                    </span>
                  </div>

                  <div class="mt-4">
                    <label
                      class="block mb-1 text-sm font-medium text-n-slate-11"
                    >
                      Mensagem do cliente
                    </label>

                    <input
                      v-model="localReplyTesterMessage"
                      type="text"
                      class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                      placeholder="Ex.: qual o pix?"
                    />
                  </div>

                  <div
                    v-if="
                      localReplyTesterResult.status ===
                      'local'
                    "
                    class="p-4 mt-4 border rounded-lg border-n-teal-6 bg-n-teal-2"
                  >
                    <div
                      class="grid grid-cols-1 gap-3 md:grid-cols-2"
                    >
                      <div>
                        <p
                          class="text-xs font-medium text-n-slate-10"
                        >
                          Categoria encontrada
                        </p>

                        <p
                          class="mt-1 text-sm font-medium text-n-slate-12"
                        >
                          {{
                            localReplyTesterResult.categoryName
                          }}
                        </p>
                      </div>

                      <div>
                        <p
                          class="text-xs font-medium text-n-slate-10"
                        >
                          Identificador
                        </p>

                        <p
                          class="mt-1 font-mono text-sm text-n-slate-12"
                        >
                          {{
                            localReplyTesterResult.categoryKey
                          }}
                        </p>
                      </div>
                    </div>

                    <div
                      class="grid grid-cols-1 gap-3 mt-4 md:grid-cols-2"
                    >
                      <div>
                        <p
                          class="text-xs font-medium text-n-slate-10"
                        >
                          Ação
                        </p>

                        <p
                          class="mt-1 text-sm font-medium text-n-slate-12"
                        >
                          {{
                            localReplyTesterResult.actionLabel
                          }}
                        </p>
                      </div>

                      <div>
                        <p
                          class="text-xs font-medium text-n-slate-10"
                        >
                          Correspondência
                        </p>

                        <p
                          class="mt-1 text-sm font-medium text-n-slate-12"
                        >
                          {{
                            localReplyTesterResult.matchModeLabel
                          }}
                        </p>
                      </div>

                      <div>
                        <p
                          class="text-xs font-medium text-n-slate-10"
                        >
                          IA utilizada
                        </p>

                        <p
                          class="mt-1 text-sm font-medium text-n-teal-11"
                        >
                          Não
                        </p>
                      </div>

                      <div
                        v-if="
                          localReplyTesterResult.shouldTransfer
                        "
                      >
                        <p
                          class="text-xs font-medium text-n-slate-10"
                        >
                          Setor
                        </p>

                        <p
                          class="mt-1 text-sm font-medium text-n-slate-12"
                        >
                          {{
                            localReplyTesterResult.sectorLabel
                          }}
                        </p>
                      </div>

                      <div
                        v-if="
                          localReplyTesterResult.shouldTransfer
                        "
                      >
                        <p
                          class="text-xs font-medium text-n-slate-10"
                        >
                          Time de destino
                        </p>

                        <p
                          class="mt-1 text-sm font-medium"
                          :class="
                            localReplyTesterResult.teamId
                              ? 'text-n-slate-12'
                              : 'text-n-ruby-11'
                          "
                        >
                          {{
                            localReplyTesterResult.teamName
                          }}
                        </p>
                      </div>
                    </div>

                    <div
                      v-if="
                        localReplyTesterResult.action !==
                        'transfer_only'
                      "
                      class="mt-4"
                    >
                      <p
                        class="text-xs font-medium text-n-slate-10"
                      >
                        Resposta final
                      </p>

                      <div
                        class="p-3 mt-1 text-sm whitespace-pre-wrap border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                      >
                        {{
                          localReplyTesterResult.reply
                        }}
                      </div>

                      <div
                        v-if="
                          localReplyTesterResult
                            .missingVariables?.length
                        "
                        class="p-3 mt-3 border rounded-lg border-n-ruby-6 bg-n-ruby-2"
                      >
                        <p
                          class="text-sm font-medium text-n-ruby-11"
                        >
                          Teste encontrou variáveis sem valor
                        </p>

                        <div
                          class="flex flex-wrap gap-2 mt-2"
                        >
                          <span
                            v-for="missingVariable in localReplyTesterResult.missingVariables"
                            :key="missingVariable"
                            class="px-2 py-1 font-mono text-xs border rounded-md border-n-ruby-6 bg-n-ruby-3 text-n-ruby-11"
                          >
                            {{ missingVariable }}
                          </span>
                        </div>

                        <p
                          class="mt-2 text-xs text-n-slate-10"
                        >
                          A resposta local seria encontrada,
                          mas esses campos precisam ser preenchidos.
                        </p>
                      </div>

                    </div>
                  </div>

                  <div
                    v-if="
                      localReplyTesterResult.status ===
                        'local' &&
                      localReplyTesterResult.action ===
                        'transfer_only'
                    "
                    class="p-3 mt-4 text-sm border rounded-lg border-n-teal-6 bg-n-teal-2 text-n-slate-12"
                  >
                    Esta regra transfere diretamente para o
                    atendimento humano sem enviar resposta da IA.
                  </div>

                  <div
                    v-else-if="
                      localReplyTesterResult.status ===
                      'ai'
                    "
                    class="p-4 mt-4 border rounded-lg border-n-amber-6 bg-n-amber-2"
                  >
                    <p
                      class="text-sm font-medium text-n-amber-11"
                    >
                      Esta mensagem seria enviada para a IA.
                    </p>

                    <p
                      class="mt-1 text-sm text-n-slate-10"
                    >
                      {{
                        localReplyTesterResult.reason
                      }}
                    </p>
                  </div>

                  <div
                    v-else-if="
                      localReplyTesterResult.status ===
                      'disabled'
                    "
                    class="p-4 mt-4 border rounded-lg border-n-ruby-6 bg-n-ruby-2"
                  >
                    <p
                      class="text-sm text-n-ruby-11"
                    >
                      {{
                        localReplyTesterResult.reason
                      }}
                    </p>
                  </div>

                  <div
                    v-else
                    class="p-4 mt-4 text-sm text-center border border-dashed rounded-lg border-n-weak text-n-slate-10"
                  >
                    {{
                      localReplyTesterResult.reason
                    }}
                  </div>
                </div>

<div>
                  <h3
                    class="text-lg font-semibold text-n-slate-12"
                  >
                    Ranking de respostas locais
                  </h3>

                  <p
                    class="mt-1 text-sm text-n-slate-11"
                  >
                    Categorias que mais responderam clientes
                    sem precisar chamar a IA neste mês.
                  </p>
                </div>

                <div
                  class="px-3 py-2 text-right border rounded-lg border-n-weak bg-n-background"
                >
                  <div
                    class="text-xs text-n-slate-10"
                  >
                    Respostas locais
                  </div>

                  <div
                    class="text-lg font-semibold text-n-slate-12"
                  >
                    {{
                      Number(
                        localUsage.month?.responses || 0
                      ).toLocaleString('pt-BR')
                    }}
                  </div>

                  <div
                    v-if="localUsage.current_month"
                    class="text-xs text-n-slate-10"
                  >
                    {{ localUsage.current_month }}
                  </div>
                </div>
              </div>

              <div
                v-if="localRuleRanking.length"
                class="mt-5 space-y-3"
              >
                <div
                  v-for="(item, index) in localRuleRanking"
                  :key="item.key"
                  class="flex flex-wrap items-center gap-3 p-3 border rounded-lg border-n-weak bg-n-background"
                >
                  <div
                    class="flex items-center justify-center w-8 h-8 text-sm font-semibold rounded-full bg-n-alpha-2 text-n-slate-12"
                  >
                    {{ index + 1 }}
                  </div>

                  <div class="flex-1 min-w-[180px]">
                    <div
                      class="font-medium text-n-slate-12"
                    >
                      {{ item.name }}
                    </div>

                    <div
                      class="mt-1 text-xs text-n-slate-10"
                    >
                      {{ item.key }}
                    </div>
                  </div>

                  <div class="text-right">
                    <div
                      class="font-semibold text-n-slate-12"
                    >
                      {{
                        item.count.toLocaleString(
                          'pt-BR'
                        )
                      }}
                    </div>

                    <div
                      class="text-xs text-n-slate-10"
                    >
                      respostas
                    </div>
                  </div>

                  <div
                    class="w-20 text-right"
                  >
                    <div
                      class="font-medium text-n-slate-12"
                    >
                      {{
                        formatLocalRankingPercentage(
                          item.percentage
                        )
                      }}%
                    </div>

                    <div
                      class="text-xs text-n-slate-10"
                    >
                      do total
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-else
                class="p-5 mt-5 text-sm text-center border border-dashed rounded-xl border-n-weak text-n-slate-11"
              >
                Ainda não há respostas locais registradas
                neste mês.
              </div>
            </div>

<div
              class="p-5 mt-5 border rounded-xl border-n-weak bg-n-alpha-1"
            >
              <div
                class="flex flex-wrap items-start justify-between gap-4"
              >
                <div>
                  <h3
                    class="text-lg font-semibold text-n-slate-12"
                  >
                    Respostas locais sem IA
                  </h3>

                  <p
                    class="mt-1 text-sm text-n-slate-11"
                  >
                    Configure respostas simples que não precisam
                    consumir tokens da inteligência artificial.
                  </p>
                </div>

                <label
                  class="flex items-center gap-3 px-3 py-2 border rounded-lg border-n-weak"
                >
                  <input
                    v-model="aiSettings.local_replies_enabled"
                    type="checkbox"
                    class="w-5 h-5"
                  />

                  <span
                    class="text-sm font-medium text-n-slate-12"
                  >
                    Ativado
                  </span>
                </label>
              </div>

              <div
                class="p-3 mt-4 text-sm rounded-lg bg-n-alpha-2 text-n-slate-11"
              >
                A correspondência continua sendo exata.
                Por exemplo,
                <strong class="text-n-slate-12">
                  horário de atendimento
                </strong>
                pode ser respondido localmente, enquanto
                <strong class="text-n-slate-12">
                  horário de atendimento e quero contratar
                </strong>
                continua indo para a IA.
              </div>

              <div
                v-if="aiSettings.local_replies_enabled"
                class="mt-6"
              >

                <div
                  v-if="localPhraseConflicts.length"
                  class="p-4 mb-5 border rounded-xl border-n-ruby-7 bg-n-ruby-3"
                >
                  <div
                    class="font-semibold text-n-ruby-11"
                  >
                    Conflito de frases encontrado
                  </div>

                  <p
                    class="mt-1 text-sm text-n-ruby-11"
                  >
                    A mesma frase está cadastrada em mais
                    de uma categoria. Corrija antes de salvar.
                  </p>

                  <div
                    class="mt-3 space-y-2"
                  >
                    <div
                      v-for="conflict in localPhraseConflicts"
                      :key="
                        conflict.phrase +
                        conflict.categories.join('-')
                      "
                      class="p-3 text-sm border rounded-lg border-n-ruby-7 bg-n-background"
                    >
                      <div
                        class="font-medium text-n-slate-12"
                      >
                        "{{ conflict.phrase }}"
                      </div>

                      <div
                        class="mt-1 text-xs text-n-slate-11"
                      >
                        Categorias:
                        {{
                          conflict.categories.join(' • ')
                        }}
                      </div>
                    </div>
                  </div>
                </div>

                                  <div
                    class="p-5 mb-5 border rounded-xl border-n-weak bg-n-alpha-1"
                  >
                    <div>
                      <h4
                        class="font-medium text-n-slate-12"
                      >
                        Biblioteca de intenções
                      </h4>

                      <p
                        class="mt-1 text-xs text-n-slate-10"
                      >
                        Adicione categorias prontas para identificar
                        mensagens sem utilizar IA. Revise as regras
                        antes de salvar.
                      </p>
                    </div>

                    <div
                      class="grid grid-cols-1 gap-3 mt-4 md:grid-cols-2 xl:grid-cols-3"
                    >
                      <button
                        v-for="intent in localIntentLibrary"
                        :key="intent.key"
                        type="button"
                        class="p-4 text-left border rounded-xl border-n-weak bg-n-background hover:bg-n-alpha-2"
                        :class="{
                          'opacity-60':
                            hasLocalIntent(intent.key),
                        }"
                        @click="addLocalIntent(intent.key)"
                      >
                        <div
                          class="flex items-center justify-between gap-3"
                        >
                          <span
                            class="text-sm font-medium text-n-slate-12"
                          >
                            {{ intent.name }}
                          </span>

                          <span
                            v-if="hasLocalIntent(intent.key)"
                            class="text-xs font-medium text-n-teal-11"
                          >
                            Adicionada
                          </span>

                          <span
                            v-else
                            class="text-xs font-medium text-n-brand"
                          >
                            + Adicionar
                          </span>
                        </div>

                        <p
                          class="mt-2 text-xs text-n-slate-10"
                        >
                          {{ intent.description }}
                        </p>

                        <p
                          class="mt-2 text-xs text-n-slate-11"
                        >
                          {{ intent.phrases.length }}
                          frases prontas
                        </p>
                      </button>
                    </div>

                    <div
                      class="p-3 mt-4 text-xs border rounded-lg border-n-weak bg-n-background text-n-slate-10"
                    >
                      Correspondências encontradas aqui são
                      processadas localmente e não utilizam IA.
                    </div>
                  </div>

<div
                  class="flex flex-wrap items-center justify-between gap-3"
                >
                  <div>
                    <h4
                      class="font-medium text-n-slate-12"
                    >
                      Categorias
                    </h4>

                    <p
                      class="mt-1 text-xs text-n-slate-10"
                    >
                      Crie quantas categorias forem necessárias
                      para esta empresa.
                    </p>
                  </div>

                  <div
                      class="flex flex-wrap items-center gap-2"
                    >
                      <select
                        v-model="selectedLocalTemplate"
                        class="px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                      >
                        <option value="">
                          Modelo por segmento
                        </option>

                        <option
                          v-for="template in localReplyTemplateOptions"
                          :key="template.key"
                          :value="template.key"
                        >
                          {{ template.name }}
                        </option>
                      </select>

                      <button
                        type="button"
                        class="px-4 py-2 text-sm font-medium border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                        @click="applyLocalReplyTemplate"
                      >
                        Aplicar modelo
                      </button>

                      <input
                        ref="localRulesImportInput"
                        type="file"
                        accept=".json,application/json"
                        class="hidden"
                        @change="importLocalRules"
                      />

                      <button
                        type="button"
                        class="px-4 py-2 text-sm font-medium border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                        @click="exportLocalRules"
                      >
                        Exportar JSON
                      </button>

                      <button
                        type="button"
                        class="px-4 py-2 text-sm font-medium border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                        @click="openLocalRulesImport"
                      >
                        Importar JSON
                      </button>

<button
                    type="button"
                    class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand"
                    @click="addLocalRule"
                  >
                    + Nova categoria
                  </button>
                    </div>
                </div>

                <div
                  v-if="!localRuleEntries.length"
                  class="p-5 mt-5 text-sm text-center border border-dashed rounded-xl border-n-weak text-n-slate-11"
                >
                  Nenhuma categoria cadastrada.

                  <button
                    type="button"
                    class="ml-1 font-medium text-n-brand"
                    @click="addLocalRule"
                  >
                    Criar primeira categoria
                  </button>
                </div>

                <div
                  v-else
                  class="mt-5 space-y-5"
                >
                  <div
                    v-for="[key, rule] in localRuleEntries"
                    :key="key"
                    class="p-4 border rounded-xl border-n-weak bg-n-background"
                  >
                    <div
                      class="flex flex-wrap items-start justify-between gap-4"
                    >
                      <div class="flex-1 min-w-[240px]">
                        <label
                          class="block mb-1 text-xs font-medium text-n-slate-10"
                        >
                          Nome da categoria
                        </label>

                        <input
                          v-model="rule.name"
                          type="text"
                          :placeholder="localRuleTitle(key, rule)"
                          class="w-full max-w-lg px-3 py-2 font-medium border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                        />

                        <div
                          class="flex items-center gap-2 mt-2 text-xs text-n-slate-10"
                        >
                          <span
                            class="inline-flex items-center px-2 py-1 font-medium rounded-md bg-n-alpha-2 text-n-slate-11"
                          >
                            {{
                              localRuleMonthCount(key)
                                .toLocaleString('pt-BR')
                            }}
                            {{
                              localRuleMonthCount(key) === 1
                                ? 'resposta neste mês'
                                : 'respostas neste mês'
                            }}
                          </span>
                        </div>
                      </div>

                      <div
                        class="flex flex-wrap items-center gap-4"
                      >
                        <label
                          class="flex items-center gap-2 text-sm text-n-slate-12"
                        >
                          <input
                            v-model="rule.enabled"
                            type="checkbox"
                          />

                          Ativo
                        </label>

                        <button
                          type="button"
                          class="px-3 py-2 text-sm border rounded-lg border-n-ruby-7 text-n-ruby-11 hover:bg-n-ruby-3"
                          @click="removeLocalRule(key)"
                        >
                          Excluir categoria
                        </button>
                      </div>
                    </div>

                    <div class="mt-4">
                      <label
                        class="block mb-1 text-sm font-medium text-n-slate-12"
                      >
                        Frases reconhecidas
                      </label>

                      <textarea
                        :value="localPhrasesText(key)"
                        rows="6"
                        placeholder="Uma frase por linha"
                        class="w-full px-3 py-3 font-mono text-sm border rounded-lg resize-y border-n-weak bg-n-background text-n-slate-12"
                        @change="
                          setLocalPhrasesText(
                            key,
                            $event.target.value
                          )
                        "
                      />

                      <p
                        class="mt-1 text-xs text-n-slate-10"
                      >
                        Uma frase ou palavra por linha.
                      </p>

                      <div class="mt-3">
                        <label
                          class="block mb-1 text-sm font-medium text-n-slate-12"
                        >
                          Correspondência
                        </label>

                        <select
                          :value="rule.match_mode || 'exact'"
                          @change="
                            rule.match_mode =
                              $event.target.value
                          "
                          class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                        >
                          <option value="exact">
                            Frase exata
                          </option>
                          <option value="contains_any">
                            Contém uma das frases
                          </option>
                        </select>

                        <p
                          class="mt-1 text-xs text-n-slate-10"
                        >
                          Frase exata exige igualdade.
                          "Contém" reconhece a frase dentro
                          de uma mensagem maior.
                        </p>
                      </div>
                    </div>

                    <div
                        class="grid grid-cols-1 gap-4 mt-4 md:grid-cols-2"
                      >
                        <div>
                          <label
                            class="block mb-1 text-sm font-medium text-n-slate-12"
                          >
                            Ação
                          </label>

                          <select
                            :value="rule.action || 'reply_only'"
                            @change="
                              rule.action = $event.target.value;
                              if (!rule.sector) {
                                rule.sector = 'geral';
                              }
                            "
                            class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                          >
                            <option value="reply_only">
                              Apenas responder
                            </option>
                            <option value="reply_and_transfer">
                              Responder e transferir
                            </option>
                            <option value="transfer_only">
                              Transferir sem responder
                            </option>
                          </select>
                        </div>

                        <div
                          v-if="
                            (rule.action || 'reply_only') !==
                            'reply_only'
                          "
                        >
                          <label
                            class="block mb-1 text-sm font-medium text-n-slate-12"
                          >
                            Setor de destino
                          </label>

                          <select
                            v-model="rule.sector"
                            class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                          >
                            <option value="geral">
                              Atendimento geral
                            </option>
                            <option value="comercial">
                              Comercial
                            </option>
                            <option value="financeiro">
                              Financeiro
                            </option>
                            <option value="suporte">
                              Suporte
                            </option>
                            <option value="cancelamento">
                              Cancelamento
                            </option>
                          </select>

                          <p class="mt-1 text-xs text-n-slate-10">
                            Usado quando nenhum time específico
                            for selecionado abaixo.
                          </p>

                          <label
                            class="block mt-4 mb-1 text-sm font-medium text-n-slate-12"
                          >
                            Time específico
                          </label>

                          <select
                            :value="rule.team_id || ''"
                            class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                            @change="
                              rule.team_id =
                                $event.target.value
                                  ? Number($event.target.value)
                                  : null
                            "
                          >
                            <option value="">
                              Usar time do setor
                            </option>

                            <option
                              v-for="team in companyTeams"
                              :key="team.id"
                              :value="team.id"
                            >
                              {{ team.name }}
                            </option>
                          </select>

                          <p class="mt-1 text-xs text-n-slate-10">
                            Opcional. Quando selecionado, este time
                            terá prioridade sobre o setor.
                          </p>
                        </div>
                      </div>

                      <div
                        v-if="
                          (rule.action || 'reply_only') !==
                          'transfer_only'
                        "
                        class="mt-4"
                      >
                      <label
                        class="block mb-1 text-sm font-medium text-n-slate-12"
                      >
                        Resposta
                      </label>

                      <textarea
                        v-model="rule.reply"
                        rows="4"
                        placeholder="Digite a resposta enviada ao cliente..."
                        class="w-full px-3 py-3 border rounded-lg resize-y border-n-weak bg-n-background text-n-slate-12"
                      />

                      <div
                        v-if="
                          getMissingLocalReplyVariables(
                            rule.reply
                          ).length
                        "
                        class="p-3 mt-3 border rounded-lg border-n-ruby-6 bg-n-ruby-2"
                      >
                        <p
                          class="text-xs font-medium text-n-ruby-11"
                        >
                          Variáveis sem valor nesta resposta
                        </p>

                        <div
                          class="flex flex-wrap gap-2 mt-2"
                        >
                          <span
                            v-for="missingVariable in getMissingLocalReplyVariables(rule.reply)"
                            :key="missingVariable"
                            class="px-2 py-1 font-mono text-xs border rounded-md border-n-ruby-6 bg-n-ruby-3 text-n-ruby-11"
                          >
                            {{ missingVariable }}
                          </span>
                        </div>

                        <p
                          class="mt-2 text-xs text-n-slate-10"
                        >
                          Cadastre um valor para essas variáveis
                          antes de usar esta resposta.
                        </p>
                      </div>


                        <div class="mt-3">
                          <p
                            class="mb-2 text-xs font-medium text-n-slate-11"
                          >
                            Variáveis disponíveis
                          </p>

                          <div
                            class="flex flex-wrap gap-2"
                          >
                            <button
                              v-for="variable in localReplyVariables"
                              :key="variable.key"
                              type="button"
                              class="inline-flex items-center px-2.5 py-1.5 font-mono text-xs border rounded-lg border-n-weak bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3"
                              :title="`Inserir ${variable.label}`"
                              @click="
                                insertLocalReplyVariable(
                                  rule,
                                  variable.key
                                )
                              "
                            >
                              {{ variable.key }}
                            </button>
                          </div>

                          <p
                            class="mt-2 text-xs text-n-slate-10"
                          >
                            Clique em uma variável para adicioná-la
                            à resposta.
                          </p>
                        </div>

                      
                    </div>
                  </div>
                </div>
              </div>
            </div>

          <CommercialFlowsEditor
            v-model="aiSettings.commercial_flows"
          />

<div
            class="p-5 mt-5 border rounded-xl border-n-weak bg-n-alpha-1"
          >
            <h3
              class="text-lg font-semibold text-n-slate-12"
            >
              Prompt da IA
            </h3>

            <p
              class="mt-1 mb-4 text-sm text-n-slate-11"
            >
              Regras, contexto e instruções utilizadas pela assistente desta empresa.
            </p>

            <textarea
              v-model="aiSettings.system_prompt"
              rows="18"
              placeholder="Digite aqui as regras e instruções da assistente..."
              class="w-full min-h-[480px] px-3 py-3 font-mono text-sm leading-6 border rounded-lg resize-y border-n-weak bg-n-background text-n-slate-12"
            />
          </div>

          <div class="flex justify-end mt-7">
            <button
              type="button"
              class="px-5 py-2 text-sm font-medium text-white rounded-lg bg-n-brand disabled:opacity-50"
              :disabled="aiSaving"
              @click="saveAiManagement"
            >
              {{
                aiSaving
                  ? 'Salvando...'
                  : 'Salvar configurações da IA'
              }}
            </button>
          </div>
        </div>

      </template>
    </div>
  </div>
</template>
